import { useState, useEffect, useRef, useMemo } from 'react';
import './design-system.css';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, signInWithCredential, updateProfile } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, collection, addDoc, getDoc, updateDoc, deleteDoc, runTransaction, getDocs, deleteField, writeBatch } from 'firebase/firestore';
import { setLogLevel } from 'firebase/firestore';
import { createRoot } from 'react-dom/client';
import PlayerStatsTabs from './components/Player/PlayerStatsTabs';
import PlayerStats from './components/Player/PlayerStats';
import PlayerCardModal from './components/Player/PlayerCardModal';
import BottomNavBar, { NAV_TABS } from './components/Navigation/BottomNavBar';
import HeaderProfileButton from './components/Navigation/HeaderProfileButton';
import EventsView from './components/Event/EventsView';
import ProfileView from './components/Player/ProfileView';
import ProfileMoreModal from './components/Player/ProfileMoreModal';
import usePlayerBadges from './components/Player/usePlayerBadges';
import { clubStandingsForWidget, isAndroidApp, saveWidgetStandings } from './utils/androidWidget';
import useAchievementAlerts from './components/Player/useAchievementAlerts';
import AchievementModal from './components/Player/AchievementModal';
import AchievementsModal from './components/Player/AchievementsModal';
import EditClubModal from './components/Modals/EditClubModal';
import TransferOwnershipModal from './components/Modals/TransferOwnershipModal';
import EditSessionModal from './components/Modals/EditSessionModal';
import EditPlayerModal from './components/Modals/EditPlayerModal';
import ConfirmationModal from './components/Modals/ConfirmationModal';
import RemovePlayerModal from './components/Modals/RemovePlayerModal';
import LeaveClubModal from './components/Modals/LeaveClubModal';
import BestOfSetsModal from './components/Modals/BestOfSetsModal';
import InvitePlayersModal from './components/Modals/InvitePlayersModal';
import { getPlayerAvatar } from './utils/player';
import { cacheAvatars, getInitialsAvatar, getResolvedAvatar, hydrateAvatarCache, subscribeAvatarCache } from './utils/avatarCache';
import { shuffleArray, generateRotation, generateClassicMatch, parseDateString } from './utils/game';
import { calculateCurrentSessionStats, calculatePairingStats, calculateMVP, calculateServer } from './utils/gameLogic';
import { calculateMatchStats, getRotationMatchOutcome } from './utils/matchStats';
import { countEventWins, migrateLegacyEvent, toDayKey, getEventStandings, getEventWinner } from './utils/eventsEngine';
import { mergeEventSources, getActiveLegacySeasonId, buildEventSummaries, withResolvedWinners } from './utils/eventsView';
import { renamePlayerInSession, renamePlayerInEvent, renamePlayerInOverallStats, renamePlayerInClub } from './utils/playerRename';
import { getMemberName, resolveSelfName, findAgreedClubName, isNameTakenByOther } from './utils/playerIdentity';
import InsightsView from './components/Insights/InsightsView';
import EventModal from './components/Event/EventModal';
import { DEFAULT_EVENT_BADGE } from './utils/eventBadges';
import LiveSession from './components/Session/LiveSession';
import LiveSessionClassic from './components/Session/LiveSessionClassic';
import CardHeader from './components/CardHeader/CardHeader';
import { logScoringEvent, fetchCurrentSessionEvents, clearCurrentSessionEvents } from './utils/scoringAudit';
import LandingPage from './components/Landing/LandingPage';
import AppVideoLanding from './components/Landing/AppVideoLanding';
import { isNativeApp as isNativePlatform } from './utils/platform';
import { initPushNotifications } from './utils/pushNotifications';
import padelMvpLogo from './assets/padelmvp_horizontal.svg'; // bundled so the header logo never pops in
import AuthModal from './components/Modals/AuthModal';


const firebaseConfig = {
  apiKey: "AIzaSyAWFzAfB08f1Lq8K0omqM0o4KeJgNCC9hU",
  authDomain: "padel-5s.firebaseapp.com",
  projectId: "padel-5s",
  storageBucket: "padel-5s.firebasestorage.app",
  messagingSenderId: "70606646192",
  appId: "1:70606646192:web:0db57d1662c78560b50477",
  measurementId: "G-4KJTRHK53Z"
};

// Required for Android Google sign-in via Chrome Custom Tabs. Get from Firebase Console > Project settings > General > Your apps (Web) or Google Cloud Console > Credentials > OAuth 2.0 Client IDs (Web client). Format: XXXXX.apps.googleusercontent.com
const GOOGLE_WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || '';

const appId = firebaseConfig.projectId;

// Firestore does not support nested arrays; we store setScores as [{ team1, team2 }, ...]
function normalizeClassicScoreFromFirestore(score) {
  if (!score) return null;
  const setScores = score.setScores;
  if (!setScores || !Array.isArray(setScores)) return { ...score, setScores: [] };
  const normalized = setScores.map((item) => {
    if (Array.isArray(item)) {
      return { team1: item[0] ?? 0, team2: item[1] ?? 0 };
    }
    if (item && typeof item === 'object') {
      return { team1: item.team1 ?? 0, team2: item.team2 ?? 0 };
    }
    return { team1: 0, team2: 0 };
  });
  return { ...score, setScores: normalized };
}

  function App() {
    const [allPlayers, setAllPlayers] = useState([]);
    // Re-render once locally cached avatars become available (see utils/avatarCache)
    const [, setAvatarCacheVersion] = useState(0);
    useEffect(() => {
      hydrateAvatarCache();
      return subscribeAvatarCache(() => setAvatarCacheVersion((v) => v + 1));
    }, []);
    const [newPlayerName, setNewPlayerName] = useState('');
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [activeNavTab, setActiveNavTab] = useState('play'); // 'play' | 'stats' | 'events' | 'insights' | 'profile'
    const [navTabDirection, setNavTabDirection] = useState('forward');

    const handleBottomTabChange = (newTab) => {
      if (newTab === activeNavTab) return;
      // Profile opens from the header avatar (#49); it sits after the nav tabs so it always slides in from the right
      const TAB_ORDER = [...NAV_TABS.map((tab) => tab.id), 'profile'];
      const oldIndex = TAB_ORDER.indexOf(activeNavTab);
      const newIndex = TAB_ORDER.indexOf(newTab);
      setNavTabDirection(newIndex > oldIndex ? 'forward' : 'backward');
      setActiveNavTab(newTab);
    };
    
    const [sessionPlayers, setSessionPlayers] = useState([]);
    const [sessionMode, setSessionMode] = useState('mvp'); // 'mvp' | 'classic'
    const [bestOfSets, setBestOfSets] = useState(3);
    const [classicScore, setClassicScore] = useState(null); // { sets: [0,0], games: [0,0], points: [0,0], isTieBreak: false }
    const [sessionVersion, setSessionVersion] = useState(0); // sessions/current.version, bumped by every classic score write
    const [classicPointHistory, setClassicPointHistory] = useState([]); // for watch sync & undo
    const [classicGameHistory, setClassicGameHistory] = useState([]); // game by game history for classic mode
    const [gameRotation, setGameRotation] = useState([]);
    const [sessionHistory, setSessionHistory] = useState([]);
    const [activeSessionStartTimestamp, setActiveSessionStartTimestamp] = useState(null);
    const [mvpLiveScore, setMvpLiveScore] = useState([0, 0]); // [team1Points, team2Points] from Firestore (watch can update)
    const [mvpConfirmGameWinFromWatch, setMvpConfirmGameWinFromWatch] = useState(null); // watch tapped confirm game win
    const [classicMatchWinFromWatch, setClassicMatchWinFromWatch] = useState(null); // watch scored classic match win
    const isArchivingRef = useRef(false);
    const [goldenPoint, setGoldenPoint] = useState(() => {
      try {
        const saved = localStorage.getItem('padelmvp_golden_point');
        return saved !== null ? JSON.parse(saved) : false;
      } catch (e) {
        return false;
      }
    });

    const handleToggleGoldenPoint = async () => {
      const next = !goldenPoint;
      setGoldenPoint(next);
      try {
        localStorage.setItem('padelmvp_golden_point', JSON.stringify(next));
      } catch (e) {}
      if (db && selectedClubId) {
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        try {
          await updateDoc(sessionDocRef, { goldenPoint: next });
        } catch (e) {
          console.error('Failed to sync goldenPoint to Firestore:', e);
        }
      }
    };
    // REMOVED: [servingOrder, setServingOrder] = useState([]); -> Replaced by logic in recordGameResult
    const [currentRotationIndex, setCurrentRotationIndex] = useState(0);
    const [currentSessionStats, setCurrentSessionStats] = useState({});
    const [archivedSessions, setArchivedSessions] = useState([]);
    // Overall standings, player cards, profile and Insights all derive from the same shared
    // matchStats module (#34), so every view shows identical W/L/GW/GL/MVP for a player.
    // The stats/overall doc is still written for other clients but no longer read here.
    const overallStats = useMemo(() => calculateMatchStats(archivedSessions), [archivedSessions]);
    
    // RENAMED STATE: Now holds all complex insights (pairing, serving, form)
    const [overallInsights, setOverallInsights] = useState({}); 

  const [playerStreaks, setPlayerStreaks] = useState({});
  
    // currentArchivedSessionIndex === null means display the latest session (archivedSessions[0])
    const [currentArchivedSessionIndex, setCurrentArchivedSessionIndex] = useState(null); 
    const [isRecalculating, setIsRecalculating] = useState(false);
    
    const [user, setUser] = useState(null);
    const [db, setDb] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isFontsReady, setIsFontsReady] = useState(false);
    const [emailForSignIn, setEmailForSignIn] = useState('');
    const [emailLinkSent, setEmailLinkSent] = useState(false);
    const [emailLinkError, setEmailLinkError] = useState('');
    const [isEmailLinkSignIn, setIsEmailLinkSignIn] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    
    const [myClubs, setMyClubs] = useState([]);
    const [selectedClubId, setSelectedClubIdState] = useState(() => {
      try {
        const s = typeof localStorage !== 'undefined' && localStorage.getItem('padel-mvp-selectedClubId');
        return (s && s.trim()) ? s.trim() : null;
      } catch (_) {
        return null;
      }
    });
    const setSelectedClubId = (id) => {
      setSelectedClubIdState(id);
      if (id) {
        setActiveNavTab('play');
      }
      try {
        if (typeof localStorage !== 'undefined') {
          if (id) localStorage.setItem('padel-mvp-selectedClubId', id);
          else localStorage.removeItem('padel-mvp-selectedClubId');
        }
      } catch (_) {}
    };
    const [clubName, setClubName] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [clubMembers, setClubMembers] = useState([]);
    
    // Removed unused state: showSessionSummary and lastSessionData
    const [gameTransition, setGameTransition] = useState(false); 
  
    const [showDeleteClubModal, setShowDeleteClubModal] = useState(false);
    const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
    const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
    const [showEditClubModal, setShowEditClubModal] = useState(false);
  const [showInvitePlayersModal, setShowInvitePlayersModal] = useState(false);
    const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showEditSessionModal, setShowEditSessionModal] = useState(false);
  const [landingPageTab, setLandingPageTab] = useState('my-clubs');
    const [showEndSessionModal, setShowEndSessionModal] = useState(false); 
    const handleEndSessionRef = useRef(null);
    const handleClassicMatchWinRef = useRef(null);
  const [showBestOfSetsModal, setShowBestOfSetsModal] = useState(false);
  const [navigationDirection, setNavigationDirection] = useState('forward'); // 'forward' or 'backward'
    const [showEditPlayerModal, setShowEditPlayerModal] = useState(false); 
    const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventModalMode, setEventModalMode] = useState('create');
  const [eventToEdit, setEventToEdit] = useState(null);
  const [eventToEnd, setEventToEnd] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const eventDeletedCallbackRef = useRef(null);
  // Events (#50) come from the new `events` collection plus legacy `seasons` not yet migrated
  const [seasonDocs, setSeasonDocs] = useState(null);
  const [eventDocs, setEventDocs] = useState(null);
  const [eventsFocusSection, setEventsFocusSection] = useState(null);
  const isEventsLoading = seasonDocs === null || eventDocs === null;
  const events = useMemo(() => mergeEventSources(seasonDocs || [], eventDocs || []), [seasonDocs, eventDocs]);
  const activeLegacySeasonId = useMemo(() => getActiveLegacySeasonId(events), [events]);
  const eventSummaries = useMemo(() => buildEventSummaries(events, archivedSessions), [events, archivedSessions]);
  // Hall of Fame / player cards: event wins from ended events (a shared win counts for each player)
  const eventMvpCounts = useMemo(
    () => countEventWins(withResolvedWinners(events, eventSummaries)),
    [events, eventSummaries]
  );
    
    const [sessionToEdit, setSessionToEdit] = useState(null);
    const [playerToTransfer, setPlayerToTransfer] = useState(null);
    const [playerToDelete, setPlayerToDelete] = useState(null);
    const [playerToEdit, setPlayerToEdit] = useState(null); 
    const [clubToEdit, setClubToEdit] = useState(null); 
    
    const [userRole, setUserRole] = useState('member');
    const [selectedPlayerForCard, setSelectedPlayerForCard] = useState(null);
    const [currentClubData, setCurrentClubData] = useState(null);

    // Download + keep a local copy of every avatar and club badge we might show
    useEffect(() => {
      cacheAvatars([
        ...allPlayers.map((p) => p?.photoUrl),
        ...clubMembers.map((m) => m?.photoUrl),
        ...myClubs.map((c) => c?.photoUrl),
        currentClubData?.photoUrl,
      ]);
    }, [allPlayers, clubMembers, myClubs, currentClubData]);
  // Badges count every club the player is in; other clubs load in the background after sign-in
  const [archivedLoadedFor, setArchivedLoadedFor] = useState(null);
  const [badgeFocusId, setBadgeFocusId] = useState(null);
  const [showAchievements, setShowAchievements] = useState(false);
  // Clubs the player has switched off in Achievement settings (stored on their profile)
  const [badgeExcludedClubs, setBadgeExcludedClubs] = useState([]);
  const playerBadges = usePlayerBadges({
    enabled: Boolean(user),
    excludedClubIds: badgeExcludedClubs,
    db,
    appId,
    user,
    myClubs,
    selectedClubId,
    currentClubData,
    archivedSessions,
    events,
    eventSummaries,
  });
    const [showCreatePlayerModal, setShowCreatePlayerModal] = useState(false);
    const [newPlayerDisplayName, setNewPlayerDisplayName] = useState('');

    const handleSaveNewPlayerName = async (e) => {
      if (e) e.preventDefault();
      const trimmed = newPlayerDisplayName.trim();
      if (!trimmed || !user) return;
      const auth = getAuth();

      try {
        if (auth.currentUser) {
          await updateProfile(auth.currentUser, { displayName: trimmed });
        }

        const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
        await setDoc(userDocRef, {
          displayName: trimmed,
          email: user.email,
          photoUrl: user.photoURL || null,
        }, { merge: true });

        setUser((prev) => ({ ...prev, displayName: trimmed }));
        setShowCreatePlayerModal(false);
      } catch (err) {
        console.error("Error saving new player name:", err);
      }
    };
    const [activeClubMenuId, setActiveClubMenuId] = useState(null);
    const [showLeaveClubModal, setShowLeaveClubModal] = useState(false);
    const [clubToLeave, setClubToLeave] = useState(null);

    const handleLeaveClub = async (clubId, newOwnerUid) => {
      if (!db || !user || !clubId) return;

      try {
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId);
        const clubSnap = await getDoc(clubDocRef);

        if (clubSnap.exists()) {
          const clubData = clubSnap.data();
          const updatedMembers = { ...(clubData.members || {}) };
          delete updatedMembers[user.uid];

          const userName = (user.displayName || '').toLowerCase().trim();
          const updatedPlayers = (clubData.players || []).filter((p) => {
            const pName = typeof p === 'string' ? p.toLowerCase().trim() : (p?.name || '').toLowerCase().trim();
            const pUid = typeof p === 'object' ? p?.uid : null;
            return pUid !== user.uid && pName !== userName;
          });

          const updatePayload = {
            members: updatedMembers,
            players: updatedPlayers,
          };

          if (newOwnerUid && updatedMembers[newOwnerUid]) {
            updatedMembers[newOwnerUid] = { ...updatedMembers[newOwnerUid], role: 'owner' };
            updatePayload.ownerId = newOwnerUid;
          }

          await updateDoc(clubDocRef, updatePayload);
        }

        const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
        await updateDoc(userDocRef, {
          [`clubs.${clubId}`]: deleteField(),
        });

        if (selectedClubId === clubId) {
          setSelectedClubId(null);
        }
      } catch (err) {
        console.error("Error leaving club:", err);
      }

      setShowLeaveClubModal(false);
      setClubToLeave(null);
    };


  
    // UseEffect to wait for Material Icons fonts to load
    useEffect(() => {
      const checkFontsLoaded = async () => {
        try {
          // Check if Font Loading API is available
          if ('fonts' in document) {
            // Wait for document.fonts to be ready
            await document.fonts.ready;
            
            // Check if fonts are loaded by trying to load them
            const fontDescriptors = [
              '24px "Material Symbols Outlined"',
              '24px "Material Symbols Rounded"'
            ];
            
            await Promise.all(
              fontDescriptors.map(descriptor => 
                document.fonts.load(descriptor).catch(() => {}) // Ignore errors
              )
            );
            
            // Additional verification: check if fonts are actually loaded
            const checkFontStatus = () => {
              try {
                const outlinedLoaded = document.fonts.check('24px "Material Symbols Outlined"');
                const roundedLoaded = document.fonts.check('24px "Material Symbols Rounded"');
                return outlinedLoaded && roundedLoaded;
              } catch {
                // If check fails, assume fonts might be loading
                return false;
              }
            };
            
            // Poll for font status with timeout
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max (50 * 100ms)
            
            const checkInterval = setInterval(() => {
              attempts++;
              if (checkFontStatus() || attempts >= maxAttempts) {
                clearInterval(checkInterval);
                setIsFontsReady(true);
              }
            }, 100);
            
            // Check immediately in case fonts are already loaded
            if (checkFontStatus()) {
              clearInterval(checkInterval);
              setIsFontsReady(true);
            }
          } else {
            // Fallback for browsers without Font Loading API
            // The fonts are already in index.html with display=block
            // Wait a reasonable amount of time for fonts to load
            setTimeout(() => {
              setIsFontsReady(true);
            }, 800);
          }
        } catch (error) {
          console.error('Error checking font loading:', error);
          // Fallback: proceed after a delay to prevent blocking
          setTimeout(() => {
            setIsFontsReady(true);
          }, 800);
        }
      };
      
      checkFontsLoaded();
    }, []);
  
  
    // Firebase initialization and authentication
    useEffect(() => {
      const initializeFirebase = async () => {
        try {
          if (!firebaseConfig.apiKey) {
            console.error("Firebase config is missing. App cannot function.");
            setIsAuthReady(true);
            return;
          }
          const app = initializeApp(firebaseConfig);
          const auth = getAuth(app);
          const firestore = getFirestore(app);
          setDb(firestore);
          setLogLevel('debug');

          // Handle redirect result (e.g. Google sign-in returning from browser on Android)
          try {
            const redirectResult = await getRedirectResult(auth);
            if (redirectResult?.user) {
              // User just signed in via redirect; onAuthStateChanged will run
            }
          } catch (err) {
            console.error('Redirect sign-in result error:', err);
          }

          // Handle Google sign-in callback from Chrome Custom Tabs (native): app was opened with ?auth_callback=1&id_token=...
          const urlParams = new URLSearchParams(window.location.search);
          const authCallbackToken = urlParams.get('id_token');
          if (urlParams.get('auth_callback') === '1' && authCallbackToken) {
            try {
              const credential = GoogleAuthProvider.credential(authCallbackToken);
              await signInWithCredential(auth, credential);
              // Clean URL so the token is not visible or re-used
              window.history.replaceState({}, document.title, window.location.pathname || '/');
            } catch (err) {
              console.error('Auth callback sign-in error:', err);
            }
          }

          // Check if this is an email link sign-in
          if (isSignInWithEmailLink(auth, window.location.href)) {
            setIsEmailLinkSignIn(true);
            // Get the email from localStorage (user entered it when requesting the link)
            const email = window.localStorage.getItem('emailForSignIn');
            
            if (!email) {
              // User opened the link on a different device. Prompt them for the email.
              setEmailLinkError('Please enter the email address you used to request the sign-in link.');
              setIsAuthReady(true);
              // Keep isEmailLinkSignIn as true so UI shows the email input
              return;
            }

            try {
              // Sign in the user with the email link
              await signInWithEmailLink(auth, email, window.location.href);
              // Clear email from storage
              window.localStorage.removeItem('emailForSignIn');
              setIsEmailLinkSignIn(false);
              // Clear URL parameters
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
              console.error("Email link sign-in failed:", error);
              setEmailLinkError(error.message || 'Sign-in link is invalid or expired. Please try again.');
              window.localStorage.removeItem('emailForSignIn');
              setIsEmailLinkSignIn(true); // Keep showing the email input in case they want to retry
              setIsAuthReady(true);
              return;
            }
          }
  
          const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser) {
              const userDocRef = doc(firestore, 'artifacts', appId, 'users', authUser.uid, 'profile', 'data');
              let existingName = authUser.displayName;

              try {
                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists() && docSnap.data().displayName) {
                  existingName = docSnap.data().displayName;
                }
              } catch (_) {}

              if (!existingName || !existingName.trim()) {
                setShowCreatePlayerModal(true);
              } else {
                setShowCreatePlayerModal(false);
              }

              setUser({
                ...authUser,
                displayName: existingName || authUser.displayName || '',
              });

              await setDoc(userDocRef, {
                displayName: existingName || authUser.displayName || null,
                email: authUser.email,
                photoUrl: authUser.photoURL || null,
              }, { merge: true });
            } else {
              setUser(null);
              setSelectedClubId(null);
              setShowCreatePlayerModal(false);
            }
            setIsAuthReady(true);
          });
          return () => unsubscribe();
        } catch (error) {
          console.error("Firebase initialization failed:", error);
          setIsAuthReady(true);
        }
      };
      initializeFirebase();
    }, []);
  
    // Firestore listener for user's club memberships
    useEffect(() => {
      const fetchClubs = async () => {
        if (isAuthReady && user && db) {
          const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
          const unsubscribeUser = onSnapshot(userDocRef, async (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setBadgeExcludedClubs(Array.isArray(data.badgeExcludedClubs) ? data.badgeExcludedClubs : []);
              if (data.clubs) {
                const staleClubIds = [];
                const clubPromises = Object.entries(data.clubs)
                  .filter(([id, name]) => name && id)
                  .map(async ([id]) => {
                    try {
                      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', id);
                      const clubSnap = await getDoc(clubDocRef);
                      if (clubSnap.exists()) {
                        const clubData = clubSnap.data();
                        return {
                          id,
                          name: clubData.name,
                          photoUrl: clubData.photoUrl || null,
                          playerCount: clubData.players ? clubData.players.length : 0,
                          ownerId: clubData.ownerId,
                          members: clubData.members || {},
                          players: clubData.players || []
                        };
                      } else {
                        staleClubIds.push(id);
                        return null;
                      }
                    } catch (_) {
                      return null;
                    }
                  });

                const fetchedClubs = await Promise.all(clubPromises);
                const validClubs = fetchedClubs.filter(club => club !== null);
                setMyClubs(validClubs);
                if (validClubs.length === 0) {
                  setLandingPageTab('create-join');
                }

                // Purge deleted/stale clubs from user profile in Firestore
                if (staleClubIds.length > 0) {
                  const updates = {};
                  staleClubIds.forEach((staleId) => {
                    updates[`clubs.${staleId}`] = deleteField();
                  });
                  updateDoc(userDocRef, updates).catch(() => {});
                }
              } else {
                setMyClubs([]);
                setLandingPageTab('create-join');
              }
            } else {
              setMyClubs([]);
              setLandingPageTab('create-join');
            }
          }, (error) => {
            console.error("Error listening to user clubs:", error);
          });
          return () => unsubscribeUser();
        }
      };
      fetchClubs();
    }, [isAuthReady, user, db, appId]);

    // Heal an account name left behind by an older club-only rename (#43): if every club I'm in
    // agrees on my name, my account adopts it. Clubs that disagree are converged by my next rename.
    const healedNameRef = useRef(null);
    useEffect(() => {
      if (!db || !user?.uid) return;
      const agreed = findAgreedClubName(myClubs, user.uid);
      if (!agreed || agreed === user.displayName || healedNameRef.current === agreed) return;
      healedNameRef.current = agreed;
      const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
      setDoc(profileRef, { displayName: agreed }, { merge: true })
        .then(() => {
          const authUser = getAuth().currentUser;
          if (authUser) updateProfile(authUser, { displayName: agreed }).catch(() => {});
          setUser((prev) => (prev ? { ...prev, displayName: agreed } : prev));
        })
        .catch((e) => console.warn('Could not sync account name with clubs:', e));
    }, [db, myClubs, user?.uid, user?.displayName]);

    // Firestore listener for selected club data
    useEffect(() => {
      if (isAuthReady && user && db && selectedClubId) {
        initPushNotifications({ db, appId, selectedClubId, user });
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        const archivedSessionsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions');
  
        const unsubscribeClub = onSnapshot(clubDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setCurrentClubData(data);
            const membersWithRoles = Object.entries(data.members || {}).map(([uid, member]) => ({ uid, ...member }));
            setClubMembers(membersWithRoles);
            setAllPlayers(data.players || []);
            
            // Set user role based on members data
            const currentUserRole = membersWithRoles.find(m => m.uid === user.uid)?.role || 'member';
            setUserRole(currentUserRole);
          } else {
            setSelectedClubId(null);
          }
        }, (error) => {
          console.error("Error listening to club data:", error);
        });
        
        const unsubscribeSession = onSnapshot(sessionDocRef, async (docSnap) => {
          if (docSnap.exists() && docSnap.data().sessionPlayers) {
            const data = docSnap.data();
            setSessionPlayers(data.sessionPlayers || []);
            setSessionHistory(data.sessionHistory || []);
            setGameRotation(data.gameRotation || []);
            setSessionMode(data.sessionMode || 'mvp');
            setBestOfSets(data.bestOfSets ?? 3);
            setClassicScore(normalizeClassicScoreFromFirestore(data.classicScore));
            setSessionVersion(typeof data.version === 'number' ? data.version : 0);
            setClassicPointHistory(data.classicPointHistory || []);
            setClassicGameHistory(data.classicGameHistory || []);
            setActiveSessionStartTimestamp(data.startTimestamp || data.createdAt || null);
            const score = Array.isArray(data.mvpLiveScore) ? data.mvpLiveScore : [0, 0];
            setMvpLiveScore(score);
            if (data.sessionMode === 'mvp') {
              console.log('[PadelMVP]', new Date().toISOString(), 'source=phone', 'event=snapshot', 'score=' + JSON.stringify(score));
            }
            if (data.mvpConfirmGameWin && typeof data.mvpConfirmGameWin.team === 'number') {
              setMvpConfirmGameWinFromWatch(data.mvpConfirmGameWin);
            }
            if (data.classicMatchWin && typeof data.classicMatchWin.team === 'number') {
              setClassicMatchWinFromWatch(data.classicMatchWin);
            }
            // Auto-detect match win in classic mode across any client (or pending classicMatchWin flag)
            if (data.sessionMode === 'classic' && data.classicScore && !isArchivingRef.current && !data.isArchiving) {
              const s1 = data.classicScore.sets?.[0] || 0;
              const s2 = data.classicScore.sets?.[1] || 0;
              const setsToWin = Math.ceil((data.bestOfSets ?? 3) / 2);
              const hasClassicMatchWin = Boolean(data.classicMatchWin && typeof data.classicMatchWin.team === 'number');
              if (s1 >= setsToWin || s2 >= setsToWin || hasClassicMatchWin) {
                const winningTeam = s1 >= setsToWin ? 1 : (s2 >= setsToWin ? 2 : (data.classicMatchWin?.team || 1));
                console.log('[AutoArchive] Classic match win detected, archiving...', { winningTeam, s1, s2, setsToWin, hasClassicMatchWin });
                if (handleClassicMatchWinRef.current) {
                  handleClassicMatchWinRef.current(winningTeam, data.classicMatchWin?.finalScore || data.classicScore, data.classicGameHistory || [], data);
                }
              }
            }
            if (data.goldenPoint !== undefined) {
              setGoldenPoint(Boolean(data.goldenPoint));
            } else {
              // Ensure existing session in Firestore gets the goldenPoint state so watches see it
              updateDoc(sessionDocRef, { goldenPoint }).catch(() => {});
            }
            if (data.pendingAction?.type === 'finish') {
              try {
                await updateDoc(sessionDocRef, { pendingAction: null });
              } catch (_) {}
              if (handleEndSessionRef.current) {
                handleEndSessionRef.current(data);
              }
            }
            if (data.pendingAction?.type === 'finishClassic' && !isArchivingRef.current && !data.isArchiving) {
              try {
                await updateDoc(sessionDocRef, { pendingAction: null });
              } catch (_) {}
              const winningTeam = data.pendingAction.team || 1;
              if (handleClassicMatchWinRef.current) {
                handleClassicMatchWinRef.current(winningTeam, data.classicScore, data.classicGameHistory || [], data);
              }
            }
            setCurrentRotationIndex(data.sessionHistory?.length % (data.gameRotation?.length || 1) || 0);
            calculateCurrentSessionStats(data.sessionHistory, data.sessionPlayers);
          } else {
            setSessionPlayers([]);
            setSessionHistory([]);
            setGameRotation([]);
            setSessionMode('mvp');
            setBestOfSets(3);
            setClassicScore(null);
            setSessionVersion(0);
            setClassicPointHistory([]);
            setMvpLiveScore([0, 0]);
            console.log('[PadelMVP]', new Date().toISOString(), 'source=phone', 'event=sessionCleared', 'score=[0,0]');
            setCurrentSessionStats({});
          }
        }, (error) => {
          console.error("Error listening to current session:", error);
        });
  
        const unsubscribeArchived = onSnapshot(archivedSessionsCollectionRef, (querySnapshot) => {
          const archivedList = [];
          let hasUnrankedRotation = false;

          querySnapshot.forEach(docSnap => {
            const data = docSnap.data();
            archivedList.push({ id: docSnap.id, ...data });
            const isClassic = data.sessionMode === 'classic' || data.classicMatch;
            const history = data.sessionHistory || [];
            if (!isClassic && history.length > 0 && (data.ranked === false || !data.mvp)) {
              hasUnrankedRotation = true;
            }
          });
          
          // Custom sort: Use parseDateString to ensure proper chronological order (most recent first)
          const sortedArchivedList = archivedList.sort((a, b) => {
              const dateA = parseDateString(a.date);
              const dateB = parseDateString(b.date);
              
              // Primary sort by date descending (latest first)
              if (dateB.getTime() !== dateA.getTime()) {
                  return dateB.getTime() - dateA.getTime();
              }
              // Secondary sort by timestamp for tie-breaking
              return b.timestamp - a.timestamp;
          });
  
          setArchivedSessions(sortedArchivedList);
          setArchivedLoadedFor(selectedClubId);

          if (hasUnrankedRotation) {
            handleRecalculateOverallStats().catch(() => {});
          }
        }, (error) => {
          console.error("Error listening to archived sessions:", error);
        });
  
        return () => {
          unsubscribeClub();
          unsubscribeSession();
          unsubscribeArchived();
        };
      } else {
        setClubMembers([]);
        setAllPlayers([]);
        setSessionPlayers([]);
        setSessionHistory([]);
        setGameRotation([]);
        // REMOVED: setServingOrder([]);
        setCurrentSessionStats({});
        setArchivedSessions([]);
      setSelectedPlayers([]); // Reset selected players when no club is selected
      }
    }, [isAuthReady, user, db, selectedClubId, appId]);
  
  // Reset selected players when switching clubs
  useEffect(() => {
    setSelectedPlayers([]);
  }, [selectedClubId]);
    
    // Re-calculate session stats whenever history changes
    useEffect(() => {
    // Check if calculateCurrentSessionStats is defined (assuming it's imported correctly)
    if (typeof calculateCurrentSessionStats === 'function') {
      const stats = calculateCurrentSessionStats(sessionHistory, sessionPlayers);
      setCurrentSessionStats(stats);
    }
    }, [sessionHistory, sessionPlayers]);
  
    // UPDATED: Calculate ALL Overall Insights whenever archived sessions change
    useEffect(() => {
      const MIN_GAMES_PAIR = 5;
      const MIN_GAMES_SERVE = 5;
    const RECENT_N_GAMES = 50; // Changed from 20 to 50 for Recent tab
  
      // 1. Initialize Aggregators
      const aggregatedPairingStats = {};
      const servingStats = {};
    const recentServingStats = {};
      let allGames = [];
    const streakTracker = {};
  
      // 2. Process Sessions
      archivedSessions.forEach(session => {
          // Collect all games for streak/form calculations, attaching session timestamp for chronological sorting
          session.sessionHistory.forEach(game => {
              allGames.push({
                  ...game,
                  sessionTimestamp: session.timestamp 
              });
          });
  
        // Aggregate pairing stats (All Time)
          const sessionPairingStats = session.pairingStats || {};
          Object.entries(sessionPairingStats).forEach(([pair, stats]) => {
              aggregatedPairingStats[pair] = aggregatedPairingStats[pair] || { wins: 0, losses: 0, totalGames: 0 };
              aggregatedPairingStats[pair].wins += stats.wins;
              aggregatedPairingStats[pair].losses += stats.losses;
              aggregatedPairingStats[pair].totalGames += (stats.wins + stats.losses);
          });
      });

    // Include live session games
    const liveSessionBaseTimestamp = Date.now();
    sessionHistory.forEach((game, index) => {
        allGames.push({
          ...game,
          sessionTimestamp: liveSessionBaseTimestamp + index
        });
    });
  
      // 3. Process All Games for Serving and Form
      // Sort all games by session timestamp (most recent last)
      allGames.sort((a, b) => a.sessionTimestamp - b.sessionTimestamp);
      
      // Slice the last N games for recent form calculation
    const recentGames = allGames.slice(-RECENT_N_GAMES);
  
      const careerStats = {}; 
      const recentStats = {}; 
    const recentPairingStats = {};
  
      // Aggregate serving stats and full career stats from all games
      allGames.forEach((game, index) => {
        const winners = Array.isArray(game.winningTeam) ? game.winningTeam : [];
        const losers = Array.isArray(game.losingTeam) ? game.losingTeam : [];
        if (winners.length === 0 && losers.length === 0) return; // Skip incomplete games (e.g. finish early)

        // a. Serving Stats (All Time)
          const server = game.serving;

        winners.forEach((player) => {
            const tracker = streakTracker[player] || { current: 0, max: 0 };
            tracker.current += 1;
            if (tracker.current > tracker.max) {
                tracker.max = tracker.current;
            }
            streakTracker[player] = tracker;
        });

        losers.forEach((player) => {
            const tracker = streakTracker[player] || { current: 0, max: 0 };
            tracker.current = 0;
            streakTracker[player] = tracker;
        });
          if (server) {
              servingStats[server] = servingStats[server] || { servingWins: 0, servingLosses: 0, totalServes: 0 };
              servingStats[server].totalServes += 1;
              
              if (winners.includes(server)) {
                  servingStats[server].servingWins += 1;
              } else {
                  servingStats[server].servingLosses += 1;
              }
          }
          
        // b. Player Form Stats (All Time)
          const teams = [...winners, ...losers];
          teams.forEach(player => {
              // Career Stats calculation
              careerStats[player] = careerStats[player] || { wins: 0, losses: 0, totalGames: 0, winRate: 0 };
              careerStats[player].totalGames += 1;
  
              if (winners.includes(player)) {
                  careerStats[player].wins += 1;
              } else {
                  careerStats[player].losses += 1;
              }
              careerStats[player].winRate = careerStats[player].wins / careerStats[player].totalGames;
          });
      });
      
    // Aggregate only the recent stats (last 50 games)
      recentGames.forEach(game => {
          const gameWinners = Array.isArray(game.winningTeam) ? game.winningTeam : [];
          const gameLosers = Array.isArray(game.losingTeam) ? game.losingTeam : [];
          if (gameWinners.length === 0 && gameLosers.length === 0) return;

          const teams = [...gameWinners, ...gameLosers];
          teams.forEach(player => {
              recentStats[player] = recentStats[player] || { wins: 0, losses: 0, totalGames: 0, winRate: 0 };
              recentStats[player].totalGames += 1;
  
              if (gameWinners.includes(player)) {
                  recentStats[player].wins += 1;
              } else {
                  recentStats[player].losses += 1;
              }
              recentStats[player].winRate = recentStats[player].wins / recentStats[player].totalGames;
          });

        // Recent pairing stats
        const winningPairKey = gameWinners.slice().sort().join(' & ');
        const losingPairKey = gameLosers.slice().sort().join(' & ');

        recentPairingStats[winningPairKey] = recentPairingStats[winningPairKey] || { wins: 0, losses: 0, totalGames: 0 };
        recentPairingStats[winningPairKey].wins += 1;
        recentPairingStats[winningPairKey].totalGames += 1;

        recentPairingStats[losingPairKey] = recentPairingStats[losingPairKey] || { wins: 0, losses: 0, totalGames: 0 };
        recentPairingStats[losingPairKey].losses += 1;
        recentPairingStats[losingPairKey].totalGames += 1;

        // Recent serving stats
        if (game.serving) {
            const server = game.serving;
            recentServingStats[server] = recentServingStats[server] || { servingWins: 0, servingLosses: 0, totalServes: 0 };
            recentServingStats[server].totalServes += 1;
            
            if (gameWinners.includes(server)) {
                recentServingStats[server].servingWins += 1;
            } else {
                recentServingStats[server].servingLosses += 1;
            }
        }
    });


    const playerMaxStreaks = {};
    Object.entries(streakTracker).forEach(([player, tracker]) => {
        playerMaxStreaks[player] = tracker.max || 0;
        if (careerStats[player]) {
            careerStats[player].longestWinStreak = tracker.max || 0;
        }
    });

    let longestWinningStreak = { player: 'N/A', streak: 0 };
    Object.entries(playerMaxStreaks).forEach(([player, streak]) => {
        if (streak > longestWinningStreak.streak) {
            longestWinningStreak = { player, streak };
        } else if (streak === longestWinningStreak.streak && streak > 0) {
            if (longestWinningStreak.player === 'N/A' || player.localeCompare(longestWinningStreak.player) < 0) {
                longestWinningStreak = { player, streak };
            }
        }
    });

    if (longestWinningStreak.streak === 0) {
        longestWinningStreak = { player: 'N/A', streak: 0 };
    }

    // 4. Determine All Time Insights
      
    // --- All Time Pairing Insights ---
    let allTimeDeadlyDuo = { pair: 'N/A', wins: -1, winPercentage: -1 };
      
      Object.entries(aggregatedPairingStats).forEach(([pair, stats]) => {
          if (stats.totalGames >= MIN_GAMES_PAIR) {
              const winPercentage = stats.wins / stats.totalGames;
              
              // Deadly Duo Logic (Highest Win %, break tie by raw Wins)
            if (winPercentage > allTimeDeadlyDuo.winPercentage) {
                allTimeDeadlyDuo = { pair, wins: stats.wins, winPercentage };
            } else if (winPercentage === allTimeDeadlyDuo.winPercentage && stats.wins > allTimeDeadlyDuo.wins) {
                allTimeDeadlyDuo = { pair, wins: stats.wins, winPercentage };
              }
          }
      });
      
    if (allTimeDeadlyDuo.wins < 0) allTimeDeadlyDuo = { pair: 'N/A', wins: 0, winPercentage: 0 };
  
    // --- All Time Serving Insights ---
    let allTimeAceMachine = { player: 'N/A', wins: -1, winRate: 0 };
    let allTimeFaultyService = { player: 'N/A', losses: -1, winRate: 1 };
  
      Object.entries(servingStats).forEach(([player, stats]) => {
          if (stats.totalServes >= MIN_GAMES_SERVE) {
              const winRate = stats.servingWins / stats.totalServes;
              
              // Ace Machine: Highest Serving Wins, break tie by rate
            if (stats.servingWins > allTimeAceMachine.wins) {
                allTimeAceMachine = { player, wins: stats.servingWins, winRate };
            } else if (stats.servingWins === allTimeAceMachine.wins && winRate > allTimeAceMachine.winRate) {
                allTimeAceMachine = { player, wins: stats.servingWins, winRate };
              }
  
              // Faulty Service: Lowest Serving Win Rate (Min 5 serves)
            if (winRate < allTimeFaultyService.winRate) {
                allTimeFaultyService = { player, losses: stats.servingLosses, winRate };
            } else if (winRate === allTimeFaultyService.winRate && stats.servingLosses > allTimeFaultyService.losses) {
                allTimeFaultyService = { player, losses: stats.servingLosses, winRate };
              }
          }
      });
    if (allTimeAceMachine.wins < 0) allTimeAceMachine = { player: 'N/A', wins: 0, winRate: 0 };
    if (allTimeFaultyService.losses < 0 || allTimeFaultyService.winRate === 1) allTimeFaultyService = { player: 'N/A', losses: 0, winRate: 0 };

    // 5. Determine Recent Insights (Last 50 Games)
    
    // --- Recent Pairing Insights ---
    let recentDeadlyDuo = { pair: 'N/A', wins: -1, winPercentage: -1 };
        
    Object.entries(recentPairingStats).forEach(([pair, stats]) => {
        if (stats.totalGames >= MIN_GAMES_PAIR) {
            const winPercentage = stats.wins / stats.totalGames;
            
            if (winPercentage > recentDeadlyDuo.winPercentage) {
                recentDeadlyDuo = { pair, wins: stats.wins, winPercentage };
            } else if (winPercentage === recentDeadlyDuo.winPercentage && stats.wins > recentDeadlyDuo.wins) {
                recentDeadlyDuo = { pair, wins: stats.wins, winPercentage };
            }
        }
    });
        
    if (recentDeadlyDuo.wins < 0) recentDeadlyDuo = { pair: 'N/A', wins: 0, winPercentage: 0 };

    // --- Recent Serving Insights ---
    let recentAceMachine = { player: 'N/A', wins: -1, winRate: 0 };
    let recentFaultyService = { player: 'N/A', losses: -1, winRate: 1 };

    Object.entries(recentServingStats).forEach(([player, stats]) => {
        if (stats.totalServes >= MIN_GAMES_SERVE) {
            const winRate = stats.servingWins / stats.totalServes;
            
            if (stats.servingWins > recentAceMachine.wins) {
                recentAceMachine = { player, wins: stats.servingWins, winRate };
            } else if (stats.servingWins === recentAceMachine.wins && winRate > recentAceMachine.winRate) {
                recentAceMachine = { player, wins: stats.servingWins, winRate };
            }

            if (winRate < recentFaultyService.winRate) {
                recentFaultyService = { player, losses: stats.servingLosses, winRate };
            } else if (winRate === recentFaultyService.winRate && stats.servingLosses > recentFaultyService.losses) {
                recentFaultyService = { player, losses: stats.servingLosses, winRate };
            }
        }
    });
    if (recentAceMachine.wins < 0) recentAceMachine = { player: 'N/A', wins: 0, winRate: 0 };
    if (recentFaultyService.losses < 0 || recentFaultyService.winRate === 1) recentFaultyService = { player: 'N/A', losses: 0, winRate: 0 };
  
    // --- Recent Form Insights (Hot/Cold Streak / On the Rise) ---
      let hotStreak = { player: 'N/A', wins: -1, winRate: 0 };
      let coldStreak = { player: 'N/A', winRate: 1 };
      let onTheRise = { player: 'N/A', improvement: -Infinity };
  
      Object.entries(recentStats).forEach(([player, recent]) => {
          const career = careerStats[player] || { totalGames: 0 };
  
        if (recent.totalGames >= MIN_GAMES_PAIR) {
              
              // Hot Streak: Highest Recent Win Rate
              if (recent.winRate > hotStreak.winRate) {
                  hotStreak = { player, wins: recent.wins, winRate: recent.winRate };
              } else if (recent.winRate === hotStreak.winRate && recent.wins > hotStreak.wins) {
                  hotStreak = { player, wins: recent.wins, winRate: recent.winRate };
              }
              
              // Cold Streak: Lowest Recent Win Rate
              if (recent.winRate < coldStreak.winRate) {
                  coldStreak = { player, winRate: recent.winRate };
              }
  
              // On the Rise: Greatest positive difference (Recent Rate - Career Rate)
              if (career.totalGames >= MIN_GAMES_PAIR) {
                  const improvement = recent.winRate - career.winRate;
                  if (improvement > onTheRise.improvement) {
                      onTheRise = { player, improvement };
                  }
              }
          }
      });
      
      if (hotStreak.wins < 0) hotStreak = { player: 'N/A', wins: 0, winRate: 0 };
      if (coldStreak.winRate === 1) coldStreak = { player: 'N/A', winRate: 0 };
      if (onTheRise.improvement === -Infinity) onTheRise = { player: 'N/A', improvement: 0 };
  
    // 6. Calculate All Time Special Insights (Most Match Wins, Most MVPs, Most Event Wins)
    // Note: These require overallStats and eventMvpCounts which are passed separately to InsightsView
    
    // 7. Final State Update
    setPlayerStreaks(playerMaxStreaks);

      setOverallInsights({
        // All Time insights
        allTime: {
            deadlyDuo: allTimeDeadlyDuo,
            aceMachine: allTimeAceMachine,
            // Note: Most Match Wins, Most MVPs, Most Event Wins, Winning Streak are calculated in InsightsView
            // from overallStats, eventMvpCounts, and longestWinningStreak
        },
        // Recent insights (last 50 games)
        recent: {
            deadlyDuo: recentDeadlyDuo,
            aceMachine: recentAceMachine,
            faultyService: recentFaultyService,
          hotStreak,
          coldStreak,
          onTheRise,
        },
        longestWinningStreak,
        recentNGames: RECENT_N_GAMES
      });
  
  }, [archivedSessions, sessionHistory]);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserDropdown]);
  
  
    const handleGoogleSignIn = async () => {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      const isNativeApp = typeof window.Capacitor !== 'undefined' && window.Capacitor.isNativePlatform?.();
      try {
        if (isNativeApp && GOOGLE_WEB_CLIENT_ID) {
          // Google blocks sign-in inside WebView (403 disallowed_useragent) and blocks custom schemes (padelmvp://) on Web Client IDs.
          // We open in Chrome Custom Tabs with a compliant HTTPS redirect_uri bridge (https://padel-5s.web.app/auth-callback.html)
          // which immediately forwards the OAuth id_token to the native padelmvp:// intent filter to complete sign-in.
          const authHost = process.env.REACT_APP_AUTH_HOST || 'https://padel-5s.web.app';
          const redirectUri = encodeURIComponent(`${authHost}/auth-callback.html`);
          const scope = encodeURIComponent('openid email profile');
          const nonce = Math.random().toString(36).slice(2) + Date.now().toString(36);
          const state = Math.random().toString(36).slice(2);
          const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(GOOGLE_WEB_CLIENT_ID)}&redirect_uri=${redirectUri}&response_type=id_token%20token&scope=${scope}&nonce=${encodeURIComponent(nonce)}&state=${encodeURIComponent(state)}`;
          const { InAppBrowser, DefaultSystemBrowserOptions } = await import('@capacitor/inappbrowser');
          await InAppBrowser.openInSystemBrowser({ url, options: DefaultSystemBrowserOptions });
          return;
        }
        if (isNativeApp) {
          // Fallback if REACT_APP_GOOGLE_WEB_CLIENT_ID is not set
          await signInWithRedirect(auth, provider);
          return;
        }
        await signInWithPopup(auth, provider);
      } catch (error) {
        console.error("Google Sign-In failed:", error);
      
      // Check if this is a storage-partitioned browser error
      if (error.code === 'auth/unauthorized-domain' || 
          error.message?.includes('missing initial state') ||
          error.message?.includes('sessionStorage')) {
        // Detect if we're in an in-app browser
        const isInAppBrowser = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent) ||
                               window.navigator.standalone === false;
        
        if (isInAppBrowser) {
          alert(
            "Sign-in requires Safari browser.\n\n" +
            "Please open this link in Safari instead of the in-app browser.\n\n" +
            "To do this:\n" +
            "1. Tap the menu (⋯) in the top right\n" +
            "2. Select 'Open in Safari'\n" +
            "3. Try signing in again"
          );
        } else {
          alert(
            "Sign-in failed due to browser storage restrictions.\n\n" +
            "Please try:\n" +
            "1. Clearing your browser cache\n" +
            "2. Opening in a regular browser (not private/incognito)\n" +
            "3. Ensuring cookies are enabled"
          );
        }
      }
      }
    };
  
    // Account settings from the profile header: edit my own player, found by uid rather than
    // name so a stale account name can't edit a phantom player
    const openAccountSettings = () => {
      const currentName = resolveSelfName(currentClubData, user?.uid, user?.displayName) || 'Player';
      const foundMember = clubMembers.find(m => m.uid === user?.uid);
      const targetPlayer = allPlayers.find(p => p.name === currentName) || foundMember || { name: currentName };
      setPlayerToEdit({ ...targetPlayer, name: currentName, uid: user?.uid });
      setShowEditPlayerModal(true);
    };

    const handleSignOut = async () => {
      const auth = getAuth();
      try {
        await signOut(auth);
        setSelectedClubId(null);
      } catch (error) {
        console.error("Sign out failed:", error);
      }
    };

    const handleEmailSignIn = async () => {
      if (!emailForSignIn.trim()) {
        setEmailLinkError('Please enter your email address');
        return;
      }

      const email = emailForSignIn.trim();
      const auth = getAuth();
      
      // Store email in localStorage for use after clicking the link
      window.localStorage.setItem('emailForSignIn', email);
      
      const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be in the authorized domains list in the Firebase Console.
        url: window.location.origin + window.location.pathname,
        // This must be true.
        handleCodeInApp: true,
      };

      try {
        setEmailLinkError('');
        setEmailLinkSent(false);
        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        // The link was successfully sent. Inform the user.
        setEmailLinkSent(true);
        setEmailLinkError('');
      } catch (error) {
        console.error("Email sign-in link failed:", error);
        if (error.code === 'auth/operation-not-allowed') {
          setEmailLinkError('Email sign-in is not enabled in Firebase Console yet. Please enable Email Link in Authentication > Sign-in method.');
        } else {
          setEmailLinkError(error.message || 'Failed to send sign-in link. Please try again.');
        }
        setEmailLinkSent(false);
        window.localStorage.removeItem('emailForSignIn');
      }
    };
  
    const handleCreateClub = async (e) => {
      e.preventDefault();
      if (!clubName.trim() || !db || !user) return;
      
      const uniqueCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      const clubId = `club_${uniqueCode}`;
      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId);
      const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
      
      try {
        const newPlayer = { name: user.displayName, photoUrl: user.photoURL };
        await setDoc(clubDocRef, {
          name: clubName.trim(),
          ownerId: user.uid, // Set owner ID
          joinCode: uniqueCode,
          members: { [user.uid]: { name: user.displayName, photoUrl: user.photoURL, role: 'owner' } }, // Set creator as owner
          players: [newPlayer],
          photoUrl: `https://placehold.co/128x128/e5e7eb/6b7280?text=${clubName.slice(0, 2).toUpperCase()}`
        });
        await setDoc(userDocRef, {
          clubs: { [clubId]: clubName.trim() },
        }, { merge: true });
        setClubName('');
      setNavigationDirection('forward');
        setSelectedClubId(clubId);
      } catch (e) {
        console.error("Error creating club:", e);
      }
    };
  
    const handleJoinClub = async (e) => {
      e.preventDefault();
      if (!joinCode.trim() || !db || !user) return;
      
      const clubId = `club_${joinCode.trim().toUpperCase()}`;
      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId);
      const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
  
      try {
        const clubSnap = await getDoc(clubDocRef);
        if (clubSnap.exists()) {
          const clubData = clubSnap.data();
          if (!clubData.members?.[user.uid]) { // Check if user is not already a member
            const newPlayer = { name: user.displayName, photoUrl: user.photoURL };
            
            // Ensure players array exists before spreading
            const existingPlayers = clubData.players || [];
            
            await updateDoc(clubDocRef, {
              [`members.${user.uid}`]: { name: user.displayName, photoUrl: user.photoURL, role: 'member' }, // Default to member
              players: [...existingPlayers, newPlayer],
            });
            await updateDoc(userDocRef, {
              [`clubs.${clubId}`]: clubData.name,
            }, { merge: true });
          }
        setNavigationDirection('forward');
          setSelectedClubId(clubId);
        } else {
          console.error("Club not found.");
        }
        setJoinCode('');
      } catch (e) {
        console.error("Error joining club:", e);
      }
    };
  
    const handleUpdateClub = async (clubId, newName, newPhotoUrl) => {
      if (!db || !selectedClubId || (userRole !== 'owner' && userRole !== 'admin')) return;
      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId);
      try {
        await updateDoc(clubDocRef, {
          name: newName,
          photoUrl: newPhotoUrl,
        });
        setShowEditClubModal(false);
      } catch (e) {
        console.error("Error updating club:", e);
      }
    };
    
    // Function to handle player name and photo updates, including history migration
    const handleUpdatePlayer = async (player, newName, newPhotoUrl) => {
      if (!db || !selectedClubId || !player) return;
      const oldName = player.name;
      const nameChanged = oldName !== newName;
      const photoChanged = newPhotoUrl !== (player.photoUrl || '');

      if (!nameChanged && !photoChanged) {
        setShowEditPlayerModal(false);
        return; // No changes needed
      }

      // Editing yourself renames you on your account and in every club you belong to (#43)
      const isSelf = Boolean(user && player.uid && player.uid === user.uid);

      try {
        // Collect all clubs where this player is a member
        // (Matching by UID avoids renaming an unrelated guest who happens to share the name in another club)
        const targetClubIds = new Set([selectedClubId]);
        if (player.uid && Array.isArray(myClubs)) {
          myClubs.forEach((c) => {
            if (c.members && c.members[player.uid]) {
              targetClubIds.add(c.id);
            }
          });
        }

        const clubs = [];
        for (const clubId of targetClubIds) {
          const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId);
          const clubSnap = await getDoc(clubDocRef);
          if (clubSnap.exists()) clubs.push({ clubId, clubDocRef, data: clubSnap.data() });
        }

        // Never merge two different people: check every club before writing anything
        if (player.uid && nameChanged) {
          const clash = clubs.find(({ data }) => isNameTakenByOther(data, player.uid, newName));
          if (clash) {
            throw new Error(`"${newName}" is already used by another player in ${clash.data.name || 'one of your clubs'}.`);
          }
        }

        const historyAndStatsWrites = [];
        const clubWrites = [];

        for (const { clubId, clubDocRef, data: clubData } of clubs) {
          // Each club renames from the name it has for this member, so clubs that drifted apart converge
          const clubOldName = getMemberName(clubData, player.uid) || oldName;
          const clubNameChanged = clubOldName !== newName;

          // 1. Club doc (narrow update, queued to execute LAST)
          const clubRes = renamePlayerInClub(clubData, clubOldName, newName, newPhotoUrl);
          if (clubRes.changed) {
            const updates = { players: clubRes.updatedData.players };
            if (clubRes.updatedData.members) updates.members = clubRes.updatedData.members;
            clubWrites.push({ ref: clubDocRef, data: updates, op: 'update' });
          }

          // If only photo changed and not name, history doesn't need to be rewritten
          if (!clubNameChanged) continue;

          // 2. Archived sessions
          const archivedRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId, 'archived_sessions');
          const archivedSnap = await getDocs(archivedRef);
          archivedSnap.docs.forEach((docSnap) => {
            const sRes = renamePlayerInSession(docSnap.data(), clubOldName, newName);
            if (sRes.changed) {
              historyAndStatsWrites.push({ ref: docSnap.ref, data: sRes.updatedData, op: 'set' });
            }
          });

          // 3. Events (seasons)
          const seasonsRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId, 'seasons');
          const seasonsSnap = await getDocs(seasonsRef);
          seasonsSnap.docs.forEach((docSnap) => {
            const eRes = renamePlayerInEvent(docSnap.data(), clubOldName, newName);
            if (eRes.changed) {
              historyAndStatsWrites.push({ ref: docSnap.ref, data: eRes.updatedData, op: 'set' });
            }
          });

          // 4. Current session
          const currentRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId, 'sessions', 'current');
          const currentSnap = await getDoc(currentRef);
          if (currentSnap.exists()) {
            const cRes = renamePlayerInSession(currentSnap.data(), clubOldName, newName);
            if (cRes.changed) {
              historyAndStatsWrites.push({ ref: currentRef, data: cRes.updatedData, op: 'set' });
            }
          }

          // 5. Overall stats
          const overallRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubId, 'stats', 'overall');
          const overallSnap = await getDoc(overallRef);
          if (overallSnap.exists()) {
            const oRes = renamePlayerInOverallStats(overallSnap.data(), clubOldName, newName);
            if (oRes.changed) {
              historyAndStatsWrites.push({ ref: overallRef, data: oRes.statsMap, op: 'set' });
            }
          }
        }

        // Your account name follows your club name, written with the club docs
        if (isSelf) {
          const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
          clubWrites.push({ ref: profileRef, data: { displayName: newName, photoUrl: newPhotoUrl || null }, op: 'merge' });
        }

        // Put club doc writes LAST so history & stats update first
        const plannedWrites = [...historyAndStatsWrites, ...clubWrites];

        // Execute writes in chunked batches (400 max per batch)
        const CHUNK_SIZE = 400;
        for (let i = 0; i < plannedWrites.length; i += CHUNK_SIZE) {
          const chunk = plannedWrites.slice(i, i + CHUNK_SIZE);
          const batch = writeBatch(db);
          for (const w of chunk) {
            if (w.op === 'update') {
              batch.update(w.ref, w.data);
            } else if (w.op === 'merge') {
              batch.set(w.ref, w.data, { merge: true });
            } else {
              batch.set(w.ref, w.data);
            }
          }
          await batch.commit();
        }

        if (isSelf && nameChanged) {
          // Auth only holds the name; photos can be data URLs too long for Auth's photoURL
          const authUser = getAuth().currentUser;
          if (authUser) await updateProfile(authUser, { displayName: newName }).catch((e) => console.warn('Could not update auth display name:', e));
          setUser((prev) => (prev ? { ...prev, displayName: newName } : prev));
        }

        setShowEditPlayerModal(false);
        setSelectedPlayerForCard(null);
        setPlayerToEdit(null);
        console.log(`Player ${oldName} updated to ${newName}.`);
      } catch (e) {
        console.error("Error updating player details and history:", e);
        alert("Failed to update player details: " + (e.message || "An error occurred"));
        setShowEditPlayerModal(true);
      }
    };

      const handleDeleteClub = async (targetClubId) => {
      const clubIdToDelete = targetClubId || selectedClubId;
      if (!db || !user || !clubIdToDelete) return;

      try {
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', clubIdToDelete);
        const clubSnap = await getDoc(clubDocRef);

        if (clubSnap.exists()) {
          const clubMembersData = clubSnap.data().members || {};
          const memberUpdates = Object.keys(clubMembersData).map(memberId => {
            const memberDocRef = doc(db, 'artifacts', appId, 'users', memberId, 'profile', 'data');
            return updateDoc(memberDocRef, {
              [`clubs.${clubIdToDelete}`]: deleteField(),
            }).catch(() => {});
          });
          await Promise.all(memberUpdates);
          await deleteDoc(clubDocRef);
        }

        if (selectedClubId === clubIdToDelete) {
          setSelectedClubId(null);
        }
        setShowDeleteClubModal(false);
        setShowEditClubModal(false);
        setClubToEdit(null);
      } catch (e) {
        console.error("Error deleting club:", e);
        setShowDeleteClubModal(false);
        setShowEditClubModal(false);
      }
    };
  
    const handleAddPlayer = async (e) => {
      e.preventDefault();
      if (!newPlayerName.trim() || !db || !selectedClubId) return;
      const newPlayer = { name: newPlayerName.trim(), photoUrl: null };
  
      try {
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
        const players = [...allPlayers, newPlayer];
        await updateDoc(clubDocRef, { players });
        setNewPlayerName('');
      } catch (e) {
        console.error("Error adding player:", e);
      }
    };

    const handleAddGuestPlayer = async (guestName) => {
      if (!guestName || !db || !selectedClubId) return;
      const trimmedName = guestName.trim();
      if (!trimmedName) return;

      const newGuestPlayer = {
        name: trimmedName,
        photoUrl: null,
        isGuest: true,
        createdAt: new Date().toISOString(),
      };

      try {
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
        const updatedPlayers = [...allPlayers, newGuestPlayer];
        await updateDoc(clubDocRef, { players: updatedPlayers });
      } catch (e) {
        console.error("Error adding guest player:", e);
        throw e;
      }
    };
  
    const handleDeletePlayer = async (player) => {
      // Permission check: Only owner or admin can remove a player
      if (!db || !selectedClubId || !player || (userRole !== 'owner' && userRole !== 'admin')) return;
      
      try {
        const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
        
        // 1. Remove from 'players' list
        const newPlayers = allPlayers.filter(p => p.name !== player.name);
        await updateDoc(clubDocRef, { players: newPlayers });
        
        // 2. If the player is a member, remove their membership entry
        if (player.uid) {
          // Use a transaction for safer concurrent update on the member's profile
          const memberProfileRef = doc(db, 'artifacts', appId, 'users', player.uid, 'profile', 'data');
          await runTransaction(db, async (transaction) => {
            // Note: Firestore updateDoc with a path set to null is used to signal removal. 
            // The clubUpdate must handle this.
            const clubUpdate = {};
            clubUpdate[`members.${player.uid}`] = null;
            transaction.update(clubDocRef, clubUpdate);
            
            transaction.update(memberProfileRef, { [`clubs.${selectedClubId}`]: null });
          });
        }
  
        setShowDeletePlayerModal(false);
        setPlayerToDelete(null);
        setSelectedPlayerForCard(null);
      } catch (e) {
        console.error("Error deleting player/member:", e);
        setShowDeletePlayerModal(false);
        setPlayerToDelete(null);
      }
    };
  
    const handlePromoteToAdmin = async (player) => {
      // Permission check: Only owner can promote/demote
      if (!db || !selectedClubId || userRole !== 'owner' || !player || player.role === 'owner') return;
  
      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
      const member = clubMembers.find(m => m.name === player.name);
      
      // Member must have a UID (must be a signed-in club member, not a guest player)
      if (!member) {
        console.warn("Cannot promote/demote a guest player.");
        return;
      }
      
      // Toggle role: admin <-> member
      const newRole = member.role === 'admin' ? 'member' : 'admin';
      try {
        await updateDoc(clubDocRef, {
          [`members.${member.uid}.role`]: newRole,
        });
        setSelectedPlayerForCard(null); // Close the modal
      } catch (e) {
        console.error("Error updating member role:", e);
      }
    };
    
    const handleTransferOwnership = async (newOwner) => {
      // Rule: Owner can only transfer to an Admin
      if (!db || !selectedClubId || userRole !== 'owner' || !newOwner || newOwner.role !== 'admin' || !user) {
        console.error("Transfer failed: Invalid permissions or target user role.");
        return;
      }
  
      const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId);
      const currentOwnerUid = user.uid;
      const newOwnerUid = newOwner.uid;
  
      try {
        await updateDoc(clubDocRef, {
          ownerId: newOwnerUid, // Update owner ID
          // Demote current owner to admin
          [`members.${currentOwnerUid}.role`]: 'admin', 
          // Promote new owner to owner
          [`members.${newOwnerUid}.role`]: 'owner', 
        });
        setShowTransferOwnershipModal(false);
        setSelectedPlayerForCard(null);
        console.log(`Ownership transferred from ${user.displayName} to ${newOwner.name}`);
      } catch (e) {
        console.error("Error transferring ownership:", e);
      }
    };
    
    // Updated to include sessionHistory to recalculate pairing stats
  const handleEditSessionStats = async (sessionId, oldStats, newStats, newDate, sessionHistory, isRanked = true) => {
      // Permission check: Only owner or admin can edit sessions
      if (!db || !selectedClubId || (userRole !== 'owner' && userRole !== 'admin')) {
        console.error("Permission denied or club not selected.");
        return;
      }
  
      setShowEditSessionModal(false);
      
    // 1. Calculate the new MVP based on the modified session stats (only if ranked)
    const newMvp = isRanked ? calculateMVP(newStats, sessionHistory) : null;
      // NEW: The original session history is still correct, so we recalculate pairing stats based on it.
      const newPairingStats = calculatePairingStats(sessionHistory);
      
      const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');
      const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions', sessionId);
  
      try {
        await runTransaction(db, async (transaction) => {
          const overallStatsDoc = await transaction.get(overallStatsDocRef);
          const currentOverallStats = overallStatsDoc.exists() ? overallStatsDoc.data() : {};
          let newOverallStats = { ...currentOverallStats };
          
        // Get old session to check if it was ranked
        const oldSession = archivedSessions.find(s => s.id === sessionId);
        const wasRanked = oldSession ? (oldSession.ranked !== false) : true; // Default to true if not set
            
        // --- 1. UNDO OLD STATS (only if session was ranked) ---
        if (wasRanked) {
          Object.keys(oldStats).forEach(playerName => {
            if (newOverallStats[playerName]) {
              const oldW = oldStats[playerName].wins || 0;
              const oldL = oldStats[playerName].losses || 0;
              
              newOverallStats[playerName].wins = (newOverallStats[playerName].wins || 0) - oldW;
              newOverallStats[playerName].losses = (newOverallStats[playerName].losses || 0) - oldL;
              newOverallStats[playerName].gamesPlayed = (newOverallStats[playerName].gamesPlayed || 0) - (oldW + oldL);
            }
          });
          
          // Undo old MVP count
          if (oldSession && oldSession.mvp && newOverallStats[oldSession.mvp]) {
             newOverallStats[oldSession.mvp].mvpCount = Math.max(0, (newOverallStats[oldSession.mvp].mvpCount || 0) - 1);
          }
          }
          
          // --- 2. UPDATE SESSION DOCUMENT (in transaction) ---
          const updatedSessionData = {
              sessionStats: newStats,
              mvp: newMvp,
              date: newDate, // Update the date field
              pairingStats: newPairingStats, // NEW: Update pairing stats
            ranked: isRanked, // Save ranked status
          };
          transaction.update(sessionDocRef, updatedSessionData);
  
        // --- 3. REDO NEW STATS (only if session is ranked) ---
        if (isRanked) {
          Object.keys(newStats).forEach(playerName => {
            if (!newOverallStats[playerName]) {
              newOverallStats[playerName] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0 };
            }
            
            const newW = newStats[playerName].wins || 0;
            const newL = newStats[playerName].losses || 0;
            
            newOverallStats[playerName].wins += newW;
            newOverallStats[playerName].losses += newL;
            newOverallStats[playerName].gamesPlayed += (newW + newL);
          });
  
          // Add new MVP count
          if (newMvp && newOverallStats[newMvp]) {
            newOverallStats[newMvp].mvpCount = (newOverallStats[newMvp].mvpCount || 0) + 1;
          }
          }
  
          // Update the overall stats document
          transaction.set(overallStatsDocRef, newOverallStats, { merge: true });
        });
  
        console.log(`Session ${sessionId} successfully updated and overall stats recalculated.`);
  
      } catch (e) {
        console.error("Error updating archived session stats:", e);
        // Revert modal visibility if save fails
        setShowEditSessionModal(true); 
      }
    };
  
  
    const handleDeleteArchivedSession = async () => {
      // Permission check: Only owner or admin can delete sessions
      if (!db || !selectedClubId || (userRole !== 'owner' && userRole !== 'admin')) return;
      
      // Determine which session to delete based on the current view index
      const targetIndex = currentArchivedSessionIndex === null ? 0 : currentArchivedSessionIndex;
      const sessionToDelete = archivedSessions[targetIndex];
      
      if (!sessionToDelete) {
          setShowDeleteSessionModal(false);
          console.error("Error: Session to delete was not found.");
          return; 
      }
  
      const sessionId = sessionToDelete.id;
      const sessionStats = sessionToDelete.sessionStats;
      const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');
      const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions', sessionId);
  
      try {
        await runTransaction(db, async (transaction) => {
          const overallStatsDoc = await transaction.get(overallStatsDocRef);
          const currentOverallStats = overallStatsDoc.exists() ? overallStatsDoc.data() : {};
          
          const newOverallStats = { ...currentOverallStats };
          
        // Only remove stats if session was ranked
        const isRanked = sessionToDelete.ranked !== false; // Default to true if not set
        
        if (isRanked) {
          Object.keys(sessionStats).forEach(playerName => {
            if (newOverallStats[playerName]) {
              newOverallStats[playerName].wins = (newOverallStats[playerName].wins || 0) - (sessionStats[playerName].wins || 0);
              newOverallStats[playerName].losses = (newOverallStats[playerName].losses || 0) - (sessionStats[playerName].losses || 0);
              newOverallStats[playerName].gamesPlayed = (newOverallStats[playerName].gamesPlayed || 0) - ((sessionStats[playerName].wins || 0) + (sessionStats[playerName].losses || 0));
              // Decrement MVP count if this session had an MVP
              if (sessionToDelete.mvp === playerName) {
                newOverallStats[playerName].mvpCount = (newOverallStats[playerName].mvpCount || 0) - 1;
              }
            }
          });
        }
          
          transaction.set(overallStatsDocRef, newOverallStats);
          transaction.delete(sessionDocRef);
        });
  
        // Update local state immediately after successful deletion
        const newArchivedSessions = archivedSessions.filter(session => session.id !== sessionId);
        setArchivedSessions(newArchivedSessions);
        // Reset the index to null to go back to the most recent session
        setCurrentArchivedSessionIndex(null);
        setShowDeleteSessionModal(false);
      } catch (e) {
        console.error("Error deleting archived session:", e);
        // Ensure modal closes even if the delete fails (after logging the error)
        setShowDeleteSessionModal(false); 
      }
    };
    
    const handleRecalculateOverallStats = async () => {
      // This function retroactively awards MVPs to rotation games, ensures all sessions are ranked,
      // and recalculates overall player stats (wins, losses, games, MVPs) across the entire club history.
      if (!db || !selectedClubId || isRecalculating) return;
  
      setIsRecalculating(true);
      try {
        const archivedSessionsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions');
        const querySnapshot = await getDocs(archivedSessionsCollectionRef);
        
        const updatePromises = [];
        const sessionsToCalc = [];
  
        querySnapshot.forEach(docSnap => {
          const sessionData = docSnap.data();
          const sessionId = docSnap.id;
          const sessionHistory = sessionData.sessionHistory || [];
          const isClassic = sessionData.sessionMode === 'classic' || sessionData.classicMatch;
          
          let sessionStats = sessionData.sessionStats;
          let mvp = sessionData.mvp;
          let pairingStats = sessionData.pairingStats; 
          let needsSessionUpdate = false;
          const sessionUpdatePayload = {};

          if (!sessionStats || Object.keys(sessionStats).length === 0) {
            if (sessionHistory.length > 0) {
              sessionStats = calculateCurrentSessionStats(sessionHistory, sessionData.sessionPlayers);
              sessionUpdatePayload.sessionStats = sessionStats;
              needsSessionUpdate = true;
            } else {
              sessionStats = {};
            }
          }

          // Rotation Games: ALWAYS award MVP regardless of when session ended, and ensure ranked = true
          if (!isClassic && sessionHistory.length > 0) {
            const calculatedMvp = calculateMVP(sessionStats, sessionHistory);
            if (calculatedMvp && (mvp !== calculatedMvp || sessionData.ranked === false || sessionData.incomplete === true)) {
              mvp = calculatedMvp;
              sessionUpdatePayload.mvp = calculatedMvp;
              sessionUpdatePayload.ranked = true;
              sessionUpdatePayload.incomplete = false;
              needsSessionUpdate = true;
            }
          }

          // Classic matches: ensure ranked = true
          if (isClassic) {
            if (sessionData.ranked === false) {
              sessionUpdatePayload.ranked = true;
              needsSessionUpdate = true;
            }
          }
          
          // Calculate pairingStats if missing
          if (!pairingStats && sessionHistory.length > 0) {
            pairingStats = calculatePairingStats(sessionHistory);
            if (Object.keys(pairingStats).length > 0) {
              sessionUpdatePayload.pairingStats = pairingStats;
              needsSessionUpdate = true;
            }
          }

          if (needsSessionUpdate) {
            const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions', sessionId);
            updatePromises.push(updateDoc(sessionDocRef, sessionUpdatePayload));
          }

          sessionsToCalc.push({ id: sessionId, ...sessionData, ...sessionUpdatePayload });
        });
        await Promise.all(updatePromises);

        const newOverallStats = calculateMatchStats(sessionsToCalc);
        const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');
        await setDoc(overallStatsDocRef, newOverallStats);
        console.log("Overall stats successfully recalculated with match stats and updated in Firestore.");
  
      } catch (e) {
        console.error("Error recalculating overall stats:", e);
      } finally {
        setIsRecalculating(false);
      }
    };
    
    const handleStartSession = async (mode = 'mvp', _unusedOrCustomOrder = null, maybeCustomOrder = null) => {
      if (!db || !selectedClubId) return;
    
      const customPlayerOrder = Array.isArray(_unusedOrCustomOrder)
        ? _unusedOrCustomOrder
        : (Array.isArray(maybeCustomOrder) ? maybeCustomOrder : null);

      setNavigationDirection('forward');
      setSessionMode(mode);
      
      // If custom player pairings / order were chosen in match setup modal, respect them!
      const sessionStartingPlayers = (customPlayerOrder && customPlayerOrder.length > 0)
        ? customPlayerOrder.map(p => typeof p === 'object' && p !== null ? p : { name: p })
        : selectedPlayers;
      
      // Update selectedPlayers so local state reflects the confirmed order
      setSelectedPlayers(sessionStartingPlayers);
      
      let newGameRotation = [];
      if (mode === 'classic' && (sessionStartingPlayers.length === 4 || sessionStartingPlayers.length === 2)) {
        const names = sessionStartingPlayers.map(p => typeof p === 'object' ? p.name : p);
        const classicMatch = sessionStartingPlayers.length === 2
          ? {
              team1: [names[0]],
              team2: [names[1]],
              server: names[0],
              id: 'singles_1',
              isTieBreak: false,
              sets: [0, 0],
              games: [0, 0],
              sitsOut: ''
            }
          : generateClassicMatch(sessionStartingPlayers, false);
        if (!classicMatch) return;
        newGameRotation = [{ ...classicMatch, sitsOut: '' }];
      } else if (sessionStartingPlayers.length >= 3 && sessionStartingPlayers.length <= 6) {
      newGameRotation = generateRotation(sessionStartingPlayers);
      if (newGameRotation.length === 0) {
        console.error("Failed to generate rotation for selected players.");
        return;
      }
      } else {
      console.error(`Incorrect number of players selected (${sessionStartingPlayers.length}). Must be between 2-6 players.`);
        return;
      }
      
      // --- RANDOM START GAME LOGIC (The new randomness source) ---
    // 1. Determine the number of possible rotation positions (varies by player count)
      const rotationSize = newGameRotation.length;
    // 2. Generate a random starting index to ensure fair rotation start (skip for Classic - single match)
  const shouldRandomizeRotationStart = mode !== 'classic' && sessionStartingPlayers.length !== 5;
  const gameOffset = shouldRandomizeRotationStart ? Math.floor(Math.random() * rotationSize) : 0;
      
      // 3. Reorder the rotation array to start at the random offset
      // Slice from offset to end, then concatenate slice from start to offset
      const randomizedGameRotation = [
      ...newGameRotation.slice(gameOffset),
      ...newGameRotation.slice(0, gameOffset)
      ];
      // --- END RANDOM START GAME LOGIC ---

      
      // *** START DEBUG LOGGING ***
      console.log("--- STARTING NEW SESSION DEBUG ---");
      console.log("Input selectedPlayers (Names - FIXED ORDER):", selectedPlayers.map(p => p.name));
  if (shouldRandomizeRotationStart) {
      console.log("Game 1 START INDEX:", gameOffset);
  } else {
      console.log("Game 1 START INDEX: 0 (5-player fixed round order)");
  }
      if (randomizedGameRotation.length > 0) {
          const game1 = randomizedGameRotation[0];
          console.log("Game 1 Team 1 (P1A & P1B):", game1.team1.join(' & '));
          console.log("Game 1 Team 2 (P2A & P2B):", game1.team2.join(' & '));
          console.log("Game 1 Sitting Out:", game1.sitsOut);
          console.log("Game 1 Server (Slot 1A):", game1.slot1A);
      }
      console.log("----------------------------------");
      // *** END DEBUG LOGGING ***

      
      const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');
      const overallStatsSnap = await getDoc(overallStatsDocRef);
      const existingOverallStats = overallStatsSnap.exists() ? overallStatsSnap.data() : {};
  
      const newOverallStats = { ...existingOverallStats };
      sessionStartingPlayers.forEach(player => {
        newOverallStats[player.name] = newOverallStats[player.name] || { wins: 0, gamesPlayed: 0, losses: 0, mvpCount: 0 };
      });
  
      try {
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        // Clear any stale scoring events left by a previous abandoned/crashed session
        await clearCurrentSessionEvents(db, appId, selectedClubId);
        const sessionStartIso = new Date().toISOString();
        await setDoc(sessionDocRef, {
          sessionPlayers: sessionStartingPlayers,
          sessionHistory: [],
          gameRotation: randomizedGameRotation,
          sessionMode: mode,
          bestOfSets: mode === 'classic' ? bestOfSets : null,
          classicScore: mode === 'classic' ? { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] } : null,
          classicPointHistory: mode === 'classic' ? [] : null,
          goldenPoint: goldenPoint,
          startTimestamp: sessionStartIso,
          createdAt: Date.now(),
          ...(mode === 'mvp' ? { mvpLiveScore: [0, 0] } : {}),
        });
        await setDoc(overallStatsDocRef, newOverallStats);
        setCurrentArchivedSessionIndex(null);
        logScoringEvent(db, appId, selectedClubId, {
          action: 'session_start',
          before: null,
          after: mode === 'classic' ? { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] } : null,
          bestOfSets: mode === 'classic' ? bestOfSets : null,
          note: `Started ${mode} session with ${sessionStartingPlayers.length} players`
        });
      } catch (e) {
        console.error("Error starting new session:", e);
      }
    };
    
    // New handler to confirm and end the session
    const confirmEndSession = () => {
      setShowEndSessionModal(true);
    };
    
    // Handler to confirm deletion of the currently displayed archived session
    const confirmDeleteArchivedSession = () => {
        // Check if a session is selected before showing the modal
        if (displayedSession.date !== 'Current Session' && displayedSession.date !== 'No Past Sessions') {
            setShowDeleteSessionModal(true);
        }
    };
  
    const handleClassicMatchWin = async (winningTeamIndex, finalScore, gameHistory, sessionDataOverride = null) => {
      if (!db || !selectedClubId) return;
      if (isArchivingRef.current) {
        console.log("[handleClassicMatchWin] Already archiving, ignoring redundant match win call");
        return;
      }
      isArchivingRef.current = true;
      setNavigationDirection('backward');

      const activePlayers = (sessionDataOverride?.sessionPlayers?.length ? sessionDataOverride.sessionPlayers : null)
        || (sessionPlayers?.length ? sessionPlayers : null)
        || [];
      const activeRotation = (sessionDataOverride?.gameRotation?.length ? sessionDataOverride.gameRotation : null)
        || (gameRotation?.length ? gameRotation : null)
        || [];
      const activeHistory = (gameHistory && gameHistory.length > 0)
        ? gameHistory
        : (sessionDataOverride?.classicGameHistory && sessionDataOverride.classicGameHistory.length > 0)
        ? sessionDataOverride.classicGameHistory
        : (classicGameHistory && classicGameHistory.length > 0)
        ? classicGameHistory
        : [];
      const activeBestOfSets = sessionDataOverride?.bestOfSets ?? bestOfSets ?? 3;

      const match = activeRotation[0] || {
        team1: activePlayers.slice(0, 2),
        team2: activePlayers.slice(2, 4)
      };
      const winningTeam = match[`team${winningTeamIndex}`] || match.team1 || [];
      const losingTeam = match[`team${winningTeamIndex === 1 ? 2 : 1}`] || match.team2 || [];
      const rawSetScores = finalScore?.setScores || sessionDataOverride?.classicScore?.setScores || classicScore?.setScores || [];
      const setScoresForFirestore = rawSetScores.map((p) =>
        Array.isArray(p) ? { team1: p[0], team2: p[1] } : { team1: p.team1 ?? 0, team2: p.team2 ?? 0 }
      );

      const matchSummaryObj = {
        winningTeam,
        losingTeam,
        team1: match.team1 || [],
        team2: match.team2 || [],
        classicMatch: true,
        sets: finalScore?.sets || sessionDataOverride?.classicScore?.sets || classicScore?.sets || [0, 0],
        setScores: setScoresForFirestore,
        sitsOut: '',
        serving: match.server || 'N/A',
        timestamp: new Date().toISOString(),
      };

      const detailedHistory = (activeHistory && activeHistory.length > 0) 
        ? activeHistory 
        : [matchSummaryObj];

      const winningNames = winningTeam.map(p => typeof p === 'object' ? p.name : p);

      const calculatedSessionStats = {};
      activePlayers.forEach(p => {
        calculatedSessionStats[typeof p === 'object' ? p.name : p] = { wins: 0, losses: 0, mvpCount: 0 };
      });

      const gamesListForStats = detailedHistory.filter(g => g.winningTeam && g.losingTeam && g.scores);
      if (gamesListForStats.length > 0) {
        gamesListForStats.forEach(g => {
          (g.winningTeam || []).forEach(name => {
            if (calculatedSessionStats[name]) calculatedSessionStats[name].wins += 1;
          });
          (g.losingTeam || []).forEach(name => {
            if (calculatedSessionStats[name]) calculatedSessionStats[name].losses += 1;
          });
        });
      } else {
        const t1Games = setScoresForFirestore.reduce((sum, s) => sum + (s.team1 || 0), 0);
        const t2Games = setScoresForFirestore.reduce((sum, s) => sum + (s.team2 || 0), 0);
        const t1Names = match.team1 || [];
        const t2Names = match.team2 || [];

        t1Names.forEach(name => {
          if (calculatedSessionStats[name]) {
            calculatedSessionStats[name].wins = t1Games;
            calculatedSessionStats[name].losses = t2Games;
          }
        });
        t2Names.forEach(name => {
          if (calculatedSessionStats[name]) {
            calculatedSessionStats[name].wins = t2Games;
            calculatedSessionStats[name].losses = t1Games;
          }
        });
      }

      try {
        const scoringEvents = await fetchCurrentSessionEvents(db, appId, selectedClubId);
        const sessionData = {
          sessionPlayers: activePlayers,
          sessionHistory: detailedHistory,
          scoringEvents,
          matchSummary: matchSummaryObj,
          sets: finalScore?.sets || sessionDataOverride?.classicScore?.sets || classicScore?.sets || [0, 0],
          setScores: setScoresForFirestore,
          bestOfSets: activeBestOfSets,
          classicMatch: true,
          sessionStats: calculatedSessionStats,
          date: new Date().toLocaleDateString('en-GB'),
          timestamp: Date.now(),
          startTimestamp: activeSessionStartTimestamp || sessionDataOverride?.startTimestamp || new Date().toISOString(),
          mvp: null, // Classic matches never award MVP
          pairingStats: {},
          ranked: true,
          sessionMode: 'classic',
        };
        const archivedSessionsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions');
        const newArchivedSessionRef = doc(archivedSessionsCollectionRef);
        const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
        const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');

        await runTransaction(db, async (transaction) => {
          const sessionDoc = await transaction.get(sessionDocRef);
          if (!sessionDoc.exists()) return;
          const currentDocData = sessionDoc.data();
          // Primary idempotency guard: if another client already archived or doc is cleared, bail out
          if (!currentDocData?.sessionPlayers || currentDocData.sessionPlayers.length === 0 || currentDocData.isArchiving) {
            return;
          }

          const overallStatsDoc = await transaction.get(overallStatsDocRef);
          let newOverallStats = overallStatsDoc.exists() ? overallStatsDoc.data() : {};
          activePlayers.forEach(p => {
            const name = typeof p === 'object' ? p.name : p;
            const pWins = calculatedSessionStats[name]?.wins || 0;
            const pLosses = calculatedSessionStats[name]?.losses || 0;
            const isWinner = winningNames.includes(name);
            if (!newOverallStats[name]) newOverallStats[name] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            newOverallStats[name].wins += (isWinner ? 1 : 0);
            newOverallStats[name].losses += (isWinner ? 0 : 1);
            newOverallStats[name].matchesWon = newOverallStats[name].wins;
            newOverallStats[name].matchesLost = newOverallStats[name].losses;
            newOverallStats[name].gw = (newOverallStats[name].gw || 0) + pWins;
            newOverallStats[name].gl = (newOverallStats[name].gl || 0) + pLosses;
            newOverallStats[name].gamesWon = newOverallStats[name].gw;
            newOverallStats[name].gamesLost = newOverallStats[name].gl;
            newOverallStats[name].gamesPlayed = (newOverallStats[name].gamesPlayed || 0) + (pWins + pLosses);
            // Classic match never awards MVP
          });
          transaction.set(overallStatsDocRef, newOverallStats);
          transaction.set(newArchivedSessionRef, sessionData);
          transaction.set(sessionDocRef, {});
        });
        await clearCurrentSessionEvents(db, appId, selectedClubId);
      } catch (e) {
        console.error('Error archiving Classic match:', e);
      } finally {
        isArchivingRef.current = false;
      }
    };
    handleClassicMatchWinRef.current = handleClassicMatchWin;

    const handleEndSession = async (sessionDataOverride = null) => {
      setShowEndSessionModal(false); // Close confirmation modal
      setNavigationDirection('backward');

      const activePlayers = (sessionDataOverride?.sessionPlayers?.length ? sessionDataOverride.sessionPlayers : null)
        || (sessionPlayers?.length ? sessionPlayers : null)
        || [];
      const activeRotation = (sessionDataOverride?.gameRotation?.length ? sessionDataOverride.gameRotation : null)
        || (gameRotation?.length ? gameRotation : null)
        || [];
      const activeScore = sessionDataOverride?.classicScore || classicScore;
      const activeHistory = sessionDataOverride?.classicGameHistory || classicGameHistory;
      const activeBestOfSets = sessionDataOverride?.bestOfSets ?? bestOfSets ?? 3;
      const currentMode = sessionDataOverride?.sessionMode || sessionMode;
      
      if (!activePlayers || activePlayers.length === 0 || !db || !selectedClubId) {
        console.error("Session not ready to end. Active players missing.");
        return;
      }

      if (currentMode === 'classic') {
        try {
          const currentMatch = activeRotation[0] || {};
          const team1 = (currentMatch.team1?.length ? currentMatch.team1 : null) || activePlayers.slice(0, 2);
          const team2 = (currentMatch.team2?.length ? currentMatch.team2 : null) || activePlayers.slice(2, 4);

          // Get completed set scores
          const completedSetScores = activeScore?.setScores || [];
          // Get in-progress games from current set
          const currentGames = activeScore?.games || [0, 0];
          const hasInProgressGames = currentGames[0] > 0 || currentGames[1] > 0 || (activeScore?.points && (activeScore.points[0] > 0 || activeScore.points[1] > 0));

          const setsToWin = Math.ceil((activeBestOfSets || 3) / 2);
          const sets = activeScore?.sets || [0, 0];
          const isMatchComplete = sets[0] >= setsToWin || sets[1] >= setsToWin;

          const winningTeam = isMatchComplete ? (sets[0] >= setsToWin ? team1 : team2) : null;
          const losingTeam = isMatchComplete ? (sets[0] >= setsToWin ? team2 : team1) : null;
          const winningNames = winningTeam ? winningTeam.map(p => typeof p === 'object' ? p.name : p) : [];

          let finalSetScores = [...completedSetScores];
          if (hasInProgressGames) {
            finalSetScores.push({ team1: currentGames[0], team2: currentGames[1] });
          }

          const matchSummaryObj = {
            team1,
            team2,
            classicMatch: true,
            sets: activeScore?.sets || [0, 0],
            setScores: finalSetScores,
            incomplete: !isMatchComplete,
            timestamp: new Date().toISOString(),
          };

          if (isMatchComplete) {
            matchSummaryObj.winningTeam = winningTeam;
            matchSummaryObj.losingTeam = losingTeam;
          }

          const detailedHistory = (activeHistory && activeHistory.length > 0) ? activeHistory : [matchSummaryObj];

          const t1Games = finalSetScores.reduce((sum, s) => sum + (s.team1 || 0), 0);
          const t2Games = finalSetScores.reduce((sum, s) => sum + (s.team2 || 0), 0);

          const calculatedSessionStats = {};
          activePlayers.forEach(p => {
            const name = typeof p === 'object' ? p.name : p;
            calculatedSessionStats[name] = { wins: 0, losses: 0, mvpCount: 0 };
          });

          const gamesListForStats = detailedHistory.filter(g => g.winningTeam && g.losingTeam && g.scores);
          if (gamesListForStats.length > 0) {
            gamesListForStats.forEach(g => {
              (g.winningTeam || []).forEach(name => {
                const n = typeof name === 'object' ? name.name : name;
                if (calculatedSessionStats[n]) calculatedSessionStats[n].wins += 1;
              });
              (g.losingTeam || []).forEach(name => {
                const n = typeof name === 'object' ? name.name : name;
                if (calculatedSessionStats[n]) calculatedSessionStats[n].losses += 1;
              });
            });
          } else {
            const t1Names = team1.map(p => typeof p === 'object' ? p.name : p);
            const t2Names = team2.map(p => typeof p === 'object' ? p.name : p);
            t1Names.forEach(name => {
              if (calculatedSessionStats[name]) {
                calculatedSessionStats[name].wins = t1Games;
                calculatedSessionStats[name].losses = t2Games;
              }
            });
            t2Names.forEach(name => {
              if (calculatedSessionStats[name]) {
                calculatedSessionStats[name].wins = t2Games;
                calculatedSessionStats[name].losses = t1Games;
              }
            });
          }

          const scoringEvents = await fetchCurrentSessionEvents(db, appId, selectedClubId);
          const sessionData = {
            sessionPlayers: activePlayers,
            sessionHistory: detailedHistory,
            scoringEvents,
            matchSummary: matchSummaryObj,
            team1,
            team2,
            sets: classicScore?.sets || [0, 0],
            setScores: finalSetScores,
            bestOfSets: bestOfSets || 3,
            classicMatch: true,
            incomplete: !isMatchComplete,
            sessionStats: calculatedSessionStats,
            date: new Date().toLocaleDateString('en-GB'),
            timestamp: Date.now(),
            startTimestamp: activeSessionStartTimestamp || new Date().toISOString(),
            mvp: null, // Classic matches never award MVP
            pairingStats: {},
            ranked: true, // All sessions are ranked
            sessionMode: 'classic',
          };

          const archivedRef = doc(collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions'));
          const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
          const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');

          await runTransaction(db, async (transaction) => {
            const currentDoc = await transaction.get(sessionDocRef);
            if (!currentDoc.exists()) return;
            const cData = currentDoc.data();
            if (!cData?.sessionPlayers || cData.sessionPlayers.length === 0 || cData.isArchiving) return;

            const overallStatsDoc = await transaction.get(overallStatsDocRef);
            let newOverallStats = overallStatsDoc.exists() ? overallStatsDoc.data() : {};
            activePlayers.forEach(p => {
              const name = typeof p === 'object' ? p.name : p;
              const pWins = calculatedSessionStats[name]?.wins || 0;
              const pLosses = calculatedSessionStats[name]?.losses || 0;
              const isWinner = isMatchComplete && winningNames.includes(name);
              const isLoser = isMatchComplete && !isWinner;
              if (!newOverallStats[name]) newOverallStats[name] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
              newOverallStats[name].wins += (isWinner ? 1 : 0);
              newOverallStats[name].losses += (isLoser ? 1 : 0);
              newOverallStats[name].matchesWon = newOverallStats[name].wins;
              newOverallStats[name].matchesLost = newOverallStats[name].losses;
              newOverallStats[name].gw = (newOverallStats[name].gw || 0) + pWins;
              newOverallStats[name].gl = (newOverallStats[name].gl || 0) + pLosses;
              newOverallStats[name].gamesWon = newOverallStats[name].gw;
              newOverallStats[name].gamesLost = newOverallStats[name].gl;
              newOverallStats[name].gamesPlayed = (newOverallStats[name].gamesPlayed || 0) + (pWins + pLosses);
              // Classic match never awards MVP
            });
            transaction.set(overallStatsDocRef, newOverallStats);
            transaction.set(archivedRef, sessionData);
            transaction.set(sessionDocRef, {});
          });
          await clearCurrentSessionEvents(db, appId, selectedClubId);
        } catch (e) {
          console.error('Error ending Classic session:', e);
        }
        return;
      }

      try {
        const mvp = calculateMVP(currentSessionStats, sessionHistory);
        const pairingStats = calculatePairingStats(sessionHistory);
        const scoringEvents = await fetchCurrentSessionEvents(db, appId, selectedClubId);

        const sessionData = {
          sessionPlayers,
          sessionHistory,
          scoringEvents,
          sessionStats: currentSessionStats,
          date: new Date().toLocaleDateString('en-GB'),
          timestamp: Date.now(),
          mvp: mvp || null,
          incomplete: false,
          pairingStats: pairingStats,
          ranked: true, // All sessions are ranked
        };

        const archivedSessionsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'archived_sessions');
        const newArchivedSessionRef = doc(archivedSessionsCollectionRef);
        // Dated events pick sessions up by date (#48); only a legacy season still lists its sessions by id
        const seasonsCollectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'seasons');
        const activeEventRef = activeLegacySeasonId ? doc(seasonsCollectionRef, activeLegacySeasonId) : null;
  
        // Use a transaction to ensure all updates are atomic.
        await runTransaction(db, async (transaction) => {
          const overallStatsDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'stats', 'overall');
          const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
  
          const currentDoc = await transaction.get(sessionDocRef);
          if (!currentDoc.exists()) return;
          const cData = currentDoc.data();
          if (!cData?.sessionPlayers || cData.sessionPlayers.length === 0 || cData.isArchiving) return;

          const overallStatsDoc = await transaction.get(overallStatsDocRef);
          const activeEventSnap = activeEventRef ? await transaction.get(activeEventRef) : null;

          let newOverallStats = overallStatsDoc.exists() ? overallStatsDoc.data() : {};
  
          const rotationOutcome = getRotationMatchOutcome({
            sessionPlayers,
            sessionStats: currentSessionStats,
            sessionHistory,
            mvp,
          });

          // Ensure session players are initialized
          (sessionPlayers || []).forEach(p => {
            const name = typeof p === 'object' ? p.name : p;
            if (name && !newOverallStats[name]) {
              newOverallStats[name] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            }
          });

          // Award match wins and losses
          rotationOutcome.winners.forEach(winner => {
            if (!newOverallStats[winner]) {
              newOverallStats[winner] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            }
            newOverallStats[winner].wins += 1;
          });
          rotationOutcome.losers.forEach(loser => {
            if (!newOverallStats[loser]) {
              newOverallStats[loser] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            }
            newOverallStats[loser].losses += 1;
          });
          if (rotationOutcome.mvpPlayer) {
            const mvpP = rotationOutcome.mvpPlayer;
            if (!newOverallStats[mvpP]) {
              newOverallStats[mvpP] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            }
            newOverallStats[mvpP].mvpCount = (newOverallStats[mvpP].mvpCount || 0) + 1;
          }

          // Update game wins/losses (GW/GL)
          Object.entries(currentSessionStats).forEach(([player, stats]) => {
            if (!newOverallStats[player]) {
              newOverallStats[player] = { wins: 0, losses: 0, gamesPlayed: 0, mvpCount: 0, gw: 0, gl: 0 };
            }
            const pWins = stats.wins || 0;
            const pLosses = stats.losses || 0;
            newOverallStats[player].gw = (newOverallStats[player].gw || 0) + pWins;
            newOverallStats[player].gl = (newOverallStats[player].gl || 0) + pLosses;
            newOverallStats[player].gamesWon = newOverallStats[player].gw;
            newOverallStats[player].gamesLost = newOverallStats[player].gl;
            newOverallStats[player].gamesPlayed = (newOverallStats[player].gamesPlayed || 0) + (pWins + pLosses);
            newOverallStats[player].matchesWon = newOverallStats[player].wins;
            newOverallStats[player].matchesLost = newOverallStats[player].losses;
          });
          
          transaction.set(overallStatsDocRef, newOverallStats);
          transaction.set(newArchivedSessionRef, sessionData);
          transaction.set(sessionDocRef, {}); // Clears current session

          if (activeEventRef && activeEventSnap && activeEventSnap.exists() && Array.isArray(activeEventSnap.data().sessionIds)) {
            const existingSessionIds = activeEventSnap.data().sessionIds;
            transaction.update(activeEventRef, {
              sessionIds: [...existingSessionIds, newArchivedSessionRef.id],
            });
          }
        });
        await clearCurrentSessionEvents(db, appId, selectedClubId);
      } catch (e) {
        console.error("Error ending session:", e);
      }
    };
    handleEndSessionRef.current = handleEndSession;
  
  const recordGameResult = async (winningTeamOrIndex, scores = null) => {
    if (!db || !selectedClubId || sessionPlayers.length < 2 || gameRotation.length === 0) {
        console.error("Session not ready to record game result.");
        return;
      }
  
      setGameTransition(true); // Start fade out
  
      // Delay the actual database update to allow the fade-out transition to run
      setTimeout(async () => {
          const currentGame = gameRotation[currentRotationIndex] || {};
          let winningTeam, losingTeam;

          if (typeof winningTeamOrIndex === 'number') {
            winningTeam = currentGame[`team${winningTeamOrIndex}`];
            losingTeam = currentGame[`team${winningTeamOrIndex === 1 ? 2 : 1}`];
          } else if (Array.isArray(winningTeamOrIndex)) {
            winningTeam = winningTeamOrIndex;
            const isTeam1 = JSON.stringify(currentGame.team1) === JSON.stringify(winningTeamOrIndex);
            losingTeam = isTeam1 ? currentGame.team2 : currentGame.team1;
          } else {
            winningTeam = currentGame.team1;
            losingTeam = currentGame.team2;
          }

          winningTeam = Array.isArray(winningTeam) ? winningTeam : (currentGame.team1 || []);
          losingTeam = Array.isArray(losingTeam) ? losingTeam : (currentGame.team2 || []);
          const sitsOutPlayers = (currentGame.sitsOut || '').split(' & ').filter(Boolean);
          
          const currentServingPlayer = calculateServer(sessionHistory.length, currentGame, sessionPlayers.length);
  
          const lastGame = sessionHistory.length > 0 ? sessionHistory[sessionHistory.length - 1] : null;
          const gameStartIso = lastGame?.timestamp || activeSessionStartTimestamp || new Date().toISOString();
          const nowIso = new Date().toISOString();

          let winPts = 4;
          let losePts = 0;
          if (scores) {
            if (typeof scores.winningTeam === 'number' || typeof scores.winningTeam === 'string') {
              winPts = scores.winningTeam;
              losePts = scores.losingTeam ?? 0;
            } else if (typeof winningTeamOrIndex === 'number') {
              winPts = scores[`team${winningTeamOrIndex}`] ?? 4;
              losePts = scores[`team${winningTeamOrIndex === 1 ? 2 : 1}`] ?? 0;
            } else {
              winPts = scores.team1 ?? 4;
              losePts = scores.team2 ?? 0;
            }
          }

          const newGame = {
              winningTeam,
              losingTeam,
              sitsOut: sitsOutPlayers.join(' & '),
              serving: currentServingPlayer || 'N/A',
              gameStartTime: gameStartIso,
              timestamp: nowIso,
              scores: { 
                winningTeam: winPts,
                losingTeam: losePts,
              },
          };
  
          const newSessionHistory = [...sessionHistory, newGame];
          
          try {
              const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
              
              await updateDoc(sessionDocRef, { 
                  sessionHistory: newSessionHistory,
                  mvpLiveScore: [0, 0],
                  mvpConfirmGameWin: null, // clear so listener does not re-trigger confirm effect
              });
              console.log('[PadelMVP]', new Date().toISOString(), 'source=phone', 'event=gameRecorded', 'score=[0,0]');
              
              setTimeout(() => {
                  setGameTransition(false); // End fade in
              }, 100); 
  
          } catch (e) {
              console.error("Error recording game result:", e);
              setGameTransition(false);
          }
      }, 300);
    };

    // When watch taps confirm game win, record the game (recordGameResult clears mvpConfirmGameWin in Firestore)
    useEffect(() => {
      if (!mvpConfirmGameWinFromWatch || !db || !selectedClubId || !appId) return;
      const payload = mvpConfirmGameWinFromWatch;
      recordGameResult(payload.team, { team1: payload.team1 ?? 0, team2: payload.team2 ?? 0 })
        .catch(() => {})
        .finally(() => setMvpConfirmGameWinFromWatch(null));
    }, [mvpConfirmGameWinFromWatch, db, selectedClubId, appId]);

    // When watch scores a classic match winning point
    useEffect(() => {
      if (!classicMatchWinFromWatch || !db || !selectedClubId || !appId) return;
      const payload = classicMatchWinFromWatch;
      setClassicMatchWinFromWatch(null);
      if (isArchivingRef.current) {
        console.log("[classicMatchWinFromWatch] Already archiving, skipping watch event effect");
        return;
      }
      handleClassicMatchWin(payload.team, payload.finalScore || payload, classicGameHistory)
        .catch((err) => console.error("Error processing watch classic match win:", err));
    }, [classicMatchWinFromWatch, db, selectedClubId, appId, classicGameHistory]);

    const handleMvpPointScored = async (team1Points, team2Points) => {
      if (!db || !selectedClubId) return;
      const sessionDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'sessions', 'current');
      const score = [team1Points, team2Points];
      try {
        await updateDoc(sessionDocRef, { mvpLiveScore: score });
        console.log('[PadelMVP]', new Date().toISOString(), 'source=phone', 'event=addPoint', 'score=' + JSON.stringify(score));
      } catch (e) {
        console.error('Failed to persist MVP point score:', e);
      }
    };
    
    // Refactored to handle "Prev" meaning "Older Session" (higher index)
    const handlePrevSession = () => {
      // Prev = Older (higher index)
      // If at default/newest (null, meaning index 0), move to index 1 (second newest).
      const currentIndex = currentArchivedSessionIndex === null ? 0 : currentArchivedSessionIndex;
      if (currentIndex < archivedSessions.length - 1) {
        setCurrentArchivedSessionIndex(currentIndex + 1); // Move to older session
      }
    };
  
    // Refactored to handle "Next" meaning "Newer Session" (lower index)
    const handleNextSession = () => {
      // Next = Newer (lower index)
      // If at index 1, move to null (default display of index 0/latest).
      if (currentArchivedSessionIndex !== null) {
        if (currentArchivedSessionIndex === 0) {
          // Should be disabled when index is 0/null, but this is a safety check
          return; 
        }
        if (currentArchivedSessionIndex === 1) {
          setCurrentArchivedSessionIndex(null); // Go back to the latest session (index 0)
        } else if (currentArchivedSessionIndex > 1) {
          setCurrentArchivedSessionIndex(currentArchivedSessionIndex - 1); // Move to newer session
        }
      }
    };
    
  const isSessionActive = sessionPlayers.length >= 2;

  // Android home-screen widget: every club's overall standings, sent once the open club and the
  // other clubs have loaded and again whenever matches change
  useEffect(() => {
    if (!isAndroidApp() || !selectedClubId || archivedLoadedFor !== selectedClubId || playerBadges.loading) return undefined;
    const timer = setTimeout(() => {
      const clubs = myClubs.map((club) => (
        club.id === selectedClubId
          ? clubStandingsForWidget({ ...club, ...currentClubData, id: club.id }, archivedSessions)
          : clubStandingsForWidget(club, playerBadges.otherClubs?.[club.id]?.archivedSessions || [])
      ));
      saveWidgetStandings(clubs);
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedClubId, archivedLoadedFor, playerBadges.loading, playerBadges.otherClubs, myClubs, currentClubData, archivedSessions]);

  // Celebrate new badges once the open club, its events and the other clubs have all loaded,
  // and never in the middle of a live match
  const [achievement, dismissAchievement] = useAchievementAlerts({
    uid: user?.uid,
    unlocked: playerBadges.unlocked,
    ready: Boolean(selectedClubId) && archivedLoadedFor === selectedClubId && !isEventsLoading && !playerBadges.loading && !isSessionActive,
  });
    const currentGame = gameRotation[currentRotationIndex] || { team1: [], team2: [], sitsOut: '' };
  const nextGameIndex = (currentRotationIndex + 1) % gameRotation.length;
  const nextGame = gameRotation[nextGameIndex] || { team1: [], team2: [], sitsOut: '' };
    
  // Calculate serving players for current and upcoming games
  const currentServingPlayer = calculateServer(sessionHistory.length, currentGame, sessionPlayers.length);
  const nextUpServingPlayer = calculateServer(sessionHistory.length + 1, nextGame, sessionPlayers.length);
  
  
    let displayedSession;
    if (isSessionActive) {
        displayedSession = {
          sessionPlayers,
          sessionHistory,
          sessionStats: currentSessionStats,
          date: 'Current Session',
          startTimestamp: activeSessionStartTimestamp || null,
          createdAt: activeSessionStartTimestamp || null,
        };
    } else if (currentArchivedSessionIndex !== null) {
        // If index is explicitly set, show that session
        displayedSession = archivedSessions[currentArchivedSessionIndex];
    } else if (archivedSessions.length > 0) {
        // If index is null (default) and sessions exist, show the latest (index 0)
        displayedSession = archivedSessions[0];
    } else {
        displayedSession = {
          sessionPlayers: [],
          sessionHistory: [],
          sessionStats: {},
          date: 'No Past Sessions'
        };
    }
    
    // Determine if the current user can delete/edit an archived session
    const canDeleteArchivedSession = userRole === 'owner' || userRole === 'admin';
    
    // Get list of current Admins for the transfer modal
    const currentAdmins = clubMembers.filter(member => member.role === 'admin');
  
    // Function to format player names for display in the Next Game module
    const formatPlayerNames = (team) => {
      // Ensure team has two players before proceeding
      if (team.length !== 2) {
        return <span>{team.join(' & ')}</span>;
      }
  
      const player1 = team[0];
      const player2 = team[1];
      
      const isPlayer1Serving = player1 === currentServingPlayer;
      const isPlayer2Serving = player2 === currentServingPlayer;
  
      return (
        <div className="flex flex-col headline-medium text-white text-center font-extrabold leading-none">
              <p className={isPlayer1Serving ? 'font-extrabold' : ''}>{player1}</p>
            <p className="body-small mt-2 mb-2 leading-none">&</p>
              <p className={isPlayer2Serving ? 'font-extrabold' : ''}>{player2}</p>
          </div>
      );
    };
    
    // Custom CSS for animated border on active session card
    const activeSessionCardStyles = `
      @keyframes live-border-animate {
        0% { box-shadow: 0 0 0 4px #2F4D94; }
        50% { box-shadow: 0 0 0 8px rgba(85, 166, 229, 0.5); }
        100% { box-shadow: 0 0 0 4px #2F4D94; }
      }
      .live-session-card {
        position: relative;
        overflow: hidden;
        animation: live-border-animate 5s infinite alternate; /* Slower 5s animation */
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
      }
      
      .vs {
        text-align: center;
        font-family: sans-serif;
        font-weight: bold;
        color: #fff;
        font-size: 1rem; /* Added font size */
      }
  
      .blue-circle {
        background: linear-gradient(
          rgb(91, 119, 189) 0%,
          rgb(91, 119, 189) 50%,
          rgb(48, 77, 149) 50%,
          rgb(48, 77, 149) 100%
        );
        border: 1px solid rgb(255, 247, 217);
        display: flex;
        flex-direction: column;
        align-items: center;
        align-content: center;
        justify-content: center;
      }
  
      .circle {
        width: 60px;
        height: 35px;
        border-radius: 32px;
        box-shadow: 0px 0px 1px 1px #0000001a;
        display: flex;
        justify-content: center;
        align-items: center;
      }
  
      .pulse-animation {
        animation: pulse-border-glow 2s infinite;
      }
  
      @keyframes pulse-border-glow {
        0% {
          box-shadow: 0 0 0 0px rgba(255, 255, 255, 0.2);
        }
        100% {
          box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); /* Reduced 20px to 10px for better fit */
        }
      }
      
      .fade-transition {
          transition: opacity 0.3s ease-in-out;
      }
      .fade-out {
          opacity: 0;
      }
      .fade-in {
          opacity: 1;
      }
      
      /* --- Serving Player Border Fix (Ditching Animated Gradient) --- */
      /* Base container for avatar to control z-index and overflow visibility */
      .player-avatar-container {
          position: relative;
          width: 52px; /* h-12 + 2*2px ring */
          height: 52px;
          margin: 0 auto; /* Center the avatar relative to its content area */
          overflow: visible; /* Allow badge to render cleanly outside */
      }
  
      /* For the serving player, ensure the standard white ring is rendered and it's on top */
      .player-avatar-container img.serving-player {
          z-index: 20; /* Highest z-index */
          border: 2px solid white; 
          box-sizing: border-box;
      }
      
      /* Badge styling */
      .serving-badge {
          position: absolute;
          bottom: -2px; /* Adjust slightly to overlap */
          left: -2px;
          z-index: 25; /* Higher than avatar for visibility */
      }
      /* --- End Serving Player Border Fix --- */
    
    /* Dropdown fade-in animation */
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-fade-in {
      animation: fadeIn 0.2s ease-out;
    }
  `;

  // Events (#50): listen to the new `events` collection and the legacy `seasons` it replaces.
  // Both are merged by id (see mergeEventSources), so unmigrated seasons still show.
  useEffect(() => {
    if (!db || !selectedClubId) {
      setSeasonDocs([]);
      setEventDocs([]);
      return;
    }
    setSeasonDocs(null);
    setEventDocs(null);

    const listen = (name, setter) => onSnapshot(
      collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, name),
      (snap) => setter(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      (error) => {
        console.error(`Error listening to ${name}:`, error);
        setter([]);
      }
    );
    const unsubscribeSeasons = listen('seasons', setSeasonDocs);
    const unsubscribeEvents = listen('events', setEventDocs);

    return () => {
      unsubscribeSeasons();
      unsubscribeEvents();
    };
  }, [db, selectedClubId]);

  // Owner and admins manage events; Firestore rules enforce the same on `events`
  const canManageEvents = userRole === 'owner' || userRole === 'admin' ||
    Boolean(currentClubData?.ownerId && currentClubData.ownerId === user?.uid);

  const getEventsCollectionRef = () => collection(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'events');
  const getSeasonDocRef = (id) => doc(db, 'artifacts', appId, 'public', 'data', 'clubs', selectedClubId, 'seasons', id);

  // A legacy season is copied into `events` the first time it is changed here. It keeps its
  // session list, so its standings stay exactly as they were.
  const getLegacyEventFields = (event) => {
    if (!event?.isLegacySeason || event.hasEventDoc) return {};
    const season = (seasonDocs || []).find((s) => s.id === event.id);
    if (!season) return {};
    const migrated = migrateLegacyEvent(season);
    return {
      name: migrated.name,
      photoUrl: season.photoUrl || null,
      status: migrated.status,
      createdAt: season.createdAt || null,
      startDate: migrated.startDate,
      endDate: migrated.endDate,
      sessionIds: Array.isArray(season.sessionIds) ? season.sessionIds : [],
      winner: event.winner || null,
    };
  };

  const openCreateEventModal = () => {
    setEventModalMode('create');
    setEventToEdit(null);
    setShowEventModal(true);
  };

  const openEditEventModal = (event) => {
    setEventModalMode('edit');
    setEventToEdit(event);
    setShowEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setEventToEdit(null);
  };

  const handleSaveEvent = async (fields) => {
    if (!db || !selectedClubId) return;

    try {
      if (eventModalMode === 'create') {
        await setDoc(doc(getEventsCollectionRef()), {
          ...fields,
          photoUrl: DEFAULT_EVENT_BADGE.url,
          status: 'active',
          createdAt: Date.now(),
          createdBy: user?.uid || null,
        });
      } else if (eventModalMode === 'edit' && eventToEdit) {
        await setDoc(doc(getEventsCollectionRef(), eventToEdit.id), {
          ...getLegacyEventFields(eventToEdit),
          ...fields,
          updatedAt: Date.now(),
        }, { merge: true });
      }

      handleCloseEventModal();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('The event could not be saved. Please try again.');
    }
  };

  const handleRequestDeleteEvent = (event, onDeleted) => {
    eventDeletedCallbackRef.current = onDeleted || null;
    setEventToDelete(event);
  };

  const handleDeleteEvent = async () => {
    const event = eventToDelete;
    if (!db || !selectedClubId || !event) return;

    try {
      if (event.hasEventDoc) await deleteDoc(doc(getEventsCollectionRef(), event.id));
      // Otherwise the legacy season would reappear in the list
      if (event.isLegacySeason) await deleteDoc(getSeasonDocRef(event.id));
      if (eventDeletedCallbackRef.current) eventDeletedCallbackRef.current();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('The event could not be deleted. Please try again.');
    } finally {
      eventDeletedCallbackRef.current = null;
      setEventToDelete(null);
    }
  };

  // Ending freezes the winner from the engine's standings at that moment
  const handleEndEvent = async () => {
    const event = eventToEnd;
    if (!db || !selectedClubId || !event) return;

    try {
      const today = toDayKey(Date.now());
      const endDate = event.endDate && event.endDate < today ? event.endDate : today;
      const endedEvent = { ...event, endDate, winner: null };
      const standings = getEventStandings(endedEvent, archivedSessions, []);
      const winner = getEventWinner(endedEvent, standings);

      await setDoc(doc(getEventsCollectionRef(), event.id), {
        ...getLegacyEventFields(event),
        status: 'ended',
        endDate,
        endTimestamp: Date.now(),
        winner: winner
          ? {
              player: winner.player,
              players: winner.players,
              value: winner.value ?? null,
              criterion: winner.criterion,
              decidedAt: new Date().toISOString(),
            }
          : null,
      }, { merge: true });

      // Stop archiving appending new sessions to the legacy season
      if (event.isLegacySeason) {
        await updateDoc(getSeasonDocRef(event.id), { status: 'ended', endTimestamp: Date.now() });
      }
    } catch (error) {
      console.error('Error ending event:', error);
      alert('The event could not be ended. Please try again.');
    } finally {
      setEventToEnd(null);
    }
  };

  const handleDeleteAccount = async (selectedNewOwners = {}) => {
    if (!db || !user) return;
    const userUid = user.uid;
    const auth = getAuth();

    try {
      if (myClubs && myClubs.length > 0) {
        const clubPromises = myClubs.map(async (club) => {
          try {
            const clubDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'clubs', club.id);
            const clubSnap = await getDoc(clubDocRef);
            if (clubSnap.exists()) {
              const clubData = clubSnap.data();
              const updatedMembers = { ...(clubData.members || {}) };
              delete updatedMembers[userUid];

              const userName = (user?.displayName || '').toLowerCase().trim();
              const updatedPlayers = (clubData.players || []).filter((p) => {
                const pName = typeof p === 'string' ? p.toLowerCase().trim() : (p?.name || '').toLowerCase().trim();
                const pUid = typeof p === 'object' ? p?.uid : null;
                return pUid !== userUid && pName !== userName;
              });

              const newOwnerUid = selectedNewOwners[club.id];
              const isOwner = clubData.ownerId === userUid || clubData.members?.[userUid]?.role === 'owner';

              const updatePayload = {
                members: updatedMembers,
                players: updatedPlayers,
              };

              if (isOwner && newOwnerUid && updatedMembers[newOwnerUid]) {
                updatedMembers[newOwnerUid] = { ...updatedMembers[newOwnerUid], role: 'owner' };
                updatePayload.ownerId = newOwnerUid;
              }

              await updateDoc(clubDocRef, updatePayload);
            }
          } catch (err) {
            console.error(`Error removing user from club ${club.id}:`, err);
          }
        });
        await Promise.all(clubPromises);
      }

      const userDocRef = doc(db, 'artifacts', appId, 'users', userUid, 'profile', 'data');
      await deleteDoc(userDocRef);

      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
        } catch (authErr) {
          console.warn("Auth delete warning, signing out:", authErr);
          await signOut(auth);
        }
      } else {
        await signOut(auth);
      }
    } catch (e) {
      console.error("Error during account deletion:", e);
      try { await signOut(auth); } catch (_) {}
    }

    setShowEditPlayerModal(false);
    setPlayerToEdit(null);
    setUser(null);
    setSelectedClubId(null);
    setMyClubs([]);
  };

  const handleRequestDeleteSession = () => {
    setShowEditSessionModal(false);
    setSessionToEdit(null);
    confirmDeleteArchivedSession();
  };
  
    if (sessionPlayers.length > 0) {
    return (
      <div className="live-session-container animate-fade-in">
        {/* Ambient Animated Blurred Wave Background */}
        <div className="ambient-live-bg">
          <div className="ambient-live-wave-1" />
          <div className="ambient-live-wave-2" />
        </div>

        <div className="relative z-10 flex-1 flex flex-col min-h-0 min-w-0">
          {showEndSessionModal && (
            <ConfirmationModal
              message="Are you sure you want to finish the current session? This will calculate MVP and archive the session stats."
              onConfirm={handleEndSession}
              onCancel={() => setShowEndSessionModal(false)}
            />
          )}
          {sessionMode === 'classic' ? (
            <LiveSessionClassic
              classicMatch={gameRotation[0]}
              classicScore={classicScore}
              sessionVersion={sessionVersion}
              classicPointHistory={classicPointHistory}
              classicGameHistory={classicGameHistory}
              bestOfSets={bestOfSets}
              goldenPoint={goldenPoint}
              onToggleGoldenPoint={handleToggleGoldenPoint}
              clubName={currentClubData?.name}
              db={db}
              selectedClubId={selectedClubId}
              appId={appId}
              allPlayers={allPlayers}
              onMatchWin={handleClassicMatchWin}
              onFinishSession={confirmEndSession}
            />
          ) : (
            <LiveSession
              currentGame={currentGame}
              nextGame={nextGame}
              currentServingPlayer={currentServingPlayer}
              nextServingPlayer={nextUpServingPlayer}
              allPlayers={allPlayers}
              onTeamWin={recordGameResult}
              sessionPlayers={sessionPlayers}
              currentSessionStats={currentSessionStats}
              sessionHistory={sessionHistory}
              gameRotation={gameRotation}
              goldenPoint={goldenPoint}
              onToggleGoldenPoint={handleToggleGoldenPoint}
              onFinishSession={confirmEndSession}
              db={db}
              selectedClubId={selectedClubId}
              appId={appId}
              clubName={currentClubData?.name}
              onPointScored={handleMvpPointScored}
              mvpLiveScore={mvpLiveScore}
            />
          )}
        </div>
      </div>
    );
  }

  if (!isAuthReady || !isFontsReady) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen bg-[#080e21]">
        <div className="card text-center p-8 bg-[#0f172a] rounded-2xl border border-slate-800">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <div className="loading-spinner"></div>
            <h2 className="card-title headline-small mb-md text-white font-bold">
              {!isFontsReady ? 'Loading...' : 'Authenticating...'}
            </h2>
            <p className="card-content text-slate-400 text-sm">
              {!isFontsReady ? 'Preparing Padel MVP...' : 'Please wait while we sign you in.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="landing-root bg-[#080e21] text-white min-h-screen">
        {/* The apps open on a video welcome screen; the web keeps the marketing landing page */}
        {isNativePlatform ? (
          <AppVideoLanding onGetStarted={() => setShowAuthModal(true)} />
        ) : (
          <LandingPage onOpenAuth={() => setShowAuthModal(true)} />
        )}
        <AuthModal
          isOpen={showAuthModal || isEmailLinkSignIn}
          onClose={() => setShowAuthModal(false)}
          handleGoogleSignIn={handleGoogleSignIn}
          handleEmailSignIn={handleEmailSignIn}
          emailForSignIn={emailForSignIn}
          setEmailForSignIn={setEmailForSignIn}
          emailLinkSent={emailLinkSent}
          setEmailLinkSent={setEmailLinkSent}
          emailLinkError={emailLinkError}
          setEmailLinkError={setEmailLinkError}
          isEmailLinkSignIn={isEmailLinkSignIn}
          onCompleteEmailLinkSignIn={async () => {
            const email = emailForSignIn.trim();
            if (!email) {
              setEmailLinkError('Please enter your email address');
              return;
            }
            const auth = getAuth();
            try {
              await signInWithEmailLink(auth, email, window.location.href);
              window.localStorage.removeItem('emailForSignIn');
              setIsEmailLinkSignIn(false);
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
              setEmailLinkError(error.message || 'Sign-in failed. Please try again.');
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="container mx-auto max-w-[680px]">
        {sessionPlayers.length === 0 && (
          <header className="app-header">
            {activeNavTab === 'profile' ? (
              // Profile header: account settings left, More menu right (Figma 791:19165)
              <button type="button" className="event-page__icon-btn" onClick={openAccountSettings} aria-label="Account settings">
                <span className="material-symbols-rounded" aria-hidden="true">edit</span>
              </button>
            ) : (
              // Invisible 48px slot so the logo/title stays centred against the profile button (Figma 787:14663)
              <span className="app-header__slot" aria-hidden="true" />
            )}
            {activeNavTab === 'play' ? (
              <img 
                src={padelMvpLogo}
                alt="Padel MVP logo" 
                width="189"
                height="36"
                decoding="sync"
                className="h-[36px] w-auto object-contain" 
              />
            ) : (
              <h1
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  fontStyle: 'italic',
                  fontWeight: 900,
                  fontSize: '24px',
                  lineHeight: '33px',
                  color: 'var(--on-surface, #f8fafc)',
                }}
                className="text-center uppercase tracking-wider my-0"
              >
                {activeNavTab === 'stats'
                  ? 'STATS'
                  : activeNavTab === 'events'
                  ? 'EVENTS'
                  : activeNavTab === 'insights'
                  ? 'INSIGHTS'
                  : 'PROFILE'}
              </h1>
            )}
            {activeNavTab === 'profile' ? (
              <button type="button" className="event-page__icon-btn" onClick={() => setShowProfileMenu(true)} aria-label="More" aria-haspopup="dialog">
                <span className="material-symbols-rounded" aria-hidden="true">more_vert</span>
              </button>
            ) : selectedClubId ? (
              <HeaderProfileButton
                avatarUrl={getPlayerAvatar(allPlayers, resolveSelfName(currentClubData, user?.uid, user?.displayName))}
                isActive={activeNavTab === 'profile'}
                onClick={() => handleBottomTabChange('profile')}
              />
            ) : (
              <span className="app-header__slot" aria-hidden="true" />
            )}
          </header>
        )}
  
          <AchievementModal
            badge={achievement?.badge}
            extra={achievement?.extra}
            onClose={dismissAchievement}
            onView={() => {
              setBadgeFocusId(achievement?.badge.id);
              dismissAchievement();
              setShowAchievements(true);
            }}
          />
          <AchievementsModal
            open={showAchievements}
            onClose={() => {
              setShowAchievements(false);
              setBadgeFocusId(null);
            }}
            badges={playerBadges}
            uid={user?.uid}
            focusId={badgeFocusId}
            clubs={myClubs}
            excludedClubIds={badgeExcludedClubs}
            onSaveExcluded={async (ids) => {
              const profileRef = doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'data');
              await setDoc(profileRef, { badgeExcludedClubs: ids }, { merge: true });
              setBadgeExcludedClubs(ids);
            }}
          />

          {/* Custom style block for the animated border */}
          <style>{activeSessionCardStyles}</style>
  
          {showDeleteClubModal && (
            <ConfirmationModal 
              message="Are you sure you want to delete this club? This action cannot be undone."
              onConfirm={handleDeleteClub}
              onCancel={() => setShowDeleteClubModal(false)}
            />
          )}
          {showDeletePlayerModal && playerToDelete && (
            <RemovePlayerModal
              player={playerToDelete}
              onConfirm={() => handleDeletePlayer(playerToDelete)}
              onClose={() => {
                setShowDeletePlayerModal(false);
                setPlayerToDelete(null);
              }}
            />
          )}
          {showDeleteSessionModal && (
            <ConfirmationModal
              message={`Are you sure you want to delete this session? This will remove all games and stats from the overall total.`}
              onConfirm={handleDeleteArchivedSession}
              onCancel={() => setShowDeleteSessionModal(false)}
            />
          )}
          {showBestOfSetsModal && (
            <BestOfSetsModal
              bestOfSets={bestOfSets}
              onBestOfChange={setBestOfSets}
              sessionStartingPlayers={selectedPlayers}
              allPlayers={allPlayers}
              onConfirm={(customPlayerOrder) => {
                setShowBestOfSetsModal(false);
                handleStartSession('classic', undefined, customPlayerOrder);
              }}
              onCancel={() => setShowBestOfSetsModal(false)}
            />
          )}
          {showEndSessionModal && (
            <ConfirmationModal
              message={`Are you sure you want to finish the current session? This will calculate MVP and archive the session stats.`}
              onConfirm={handleEndSession}
              onCancel={() => setShowEndSessionModal(false)}
            />
          )}
          {selectedPlayerForCard && (
            <PlayerCardModal
              player={selectedPlayerForCard}
              overallStats={overallStats}
              onClose={() => setSelectedPlayerForCard(null)}
              onPromote={handlePromoteToAdmin}
              onTransferOwnership={(player) => {
                setPlayerToTransfer(player);
                setShowTransferOwnershipModal(true);
                setSelectedPlayerForCard(null);
              }}
              onRemove={(player) => {
                setPlayerToDelete(player);
                setShowDeletePlayerModal(true);
                setSelectedPlayerForCard(null);
              }}
              onEdit={(player) => { 
                  setPlayerToEdit(player);
                  setShowEditPlayerModal(true);
                  setSelectedPlayerForCard(null); 
              }} 
              userRole={userRole}
              authUserUid={user?.uid}
            playerStreaks={playerStreaks}
            eventMvpCounts={eventMvpCounts}
            />
          )}
  
          {/* Edit Club Modal */}
          {showEditClubModal && (clubToEdit || currentClubData) && (
            <EditClubModal
              club={clubToEdit || { id: selectedClubId, name: currentClubData.name, photoUrl: currentClubData.photoUrl, joinCode: currentClubData.joinCode, ownerId: currentClubData.ownerId, playerCount: currentClubData.playerCount }}
              onSave={handleUpdateClub}
              onClose={() => {
                setShowEditClubModal(false);
                setClubToEdit(null);
              }}
              onDelete={handleDeleteClub}
              userRole={(clubToEdit || currentClubData)?.ownerId === user?.uid || (clubToEdit || currentClubData)?.members?.[user?.uid]?.role === 'owner' ? 'owner' : userRole}
            />
          )}

          {/* Leave Club Modal */}
          {showLeaveClubModal && clubToLeave && (
            <LeaveClubModal
              club={clubToLeave}
              user={user}
              allPlayers={allPlayers}
              onClose={() => {
                setShowLeaveClubModal(false);
                setClubToLeave(null);
              }}
              onConfirmLeave={handleLeaveClub}
            />
          )}

        {showInvitePlayersModal && currentClubData && user && (
          <InvitePlayersModal
            clubName={currentClubData.name}
            joinCode={currentClubData.joinCode}
            playerFirstName={user.displayName?.split(' ')[0] || 'A player'}
            allPlayers={allPlayers}
            onAddGuest={handleAddGuestPlayer}
            onClose={() => setShowInvitePlayersModal(false)}
          />
        )}
          
          {showTransferOwnershipModal && playerToTransfer && (
            <TransferOwnershipModal
              admins={currentAdmins}
              clubName={currentClubData?.name}
              onTransfer={handleTransferOwnership}
              onClose={() => setShowTransferOwnershipModal(false)}
              authUserUid={user.uid}
            />
          )}
          
          {showEditSessionModal && sessionToEdit && (
            <EditSessionModal
              session={sessionToEdit}
              allPlayers={allPlayers}
              onClose={() => {
                setShowEditSessionModal(false);
                setSessionToEdit(null);
              }}
              onSave={handleEditSessionStats}
              onDelete={handleRequestDeleteSession}
            />
          )}
          
          {/* Create Player Modal (First time login without display name) */}
          {user && (showCreatePlayerModal || (!user.displayName && isAuthReady)) && (
            <div className="modal-overlay">
              <div className="modal">
                <h3 className="modal-title">Create player</h3>

                <div className="modal-body">
                  <p className="modal-text modal-text-strong">Welcome to Padel MVP!</p>
                  <p className="modal-text">Add your player name to get started</p>
                </div>

                <form onSubmit={handleSaveNewPlayerName}>
                  <input
                    type="text"
                    placeholder="Player name"
                    value={newPlayerDisplayName}
                    onChange={(e) => setNewPlayerDisplayName(e.target.value)}
                    className="input w-full"
                    autoFocus
                  />

                  <div className="modal-actions">
                    <button
                      type="submit"
                      disabled={!newPlayerDisplayName.trim()}
                      className="btn btn-primary btn-full"
                    >
                      <span className="btn-text">NEXT</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* NEW: Edit Player Modal */}
          {showEditPlayerModal && playerToEdit && (
            <EditPlayerModal
              player={playerToEdit}
              allPlayers={allPlayers}
              clubMembers={clubMembers}
              onClose={() => {
                setShowEditPlayerModal(false);
                setPlayerToEdit(null);
              }}
              onSave={handleUpdatePlayer}
              user={user} 
              playerStreaks={playerStreaks}
              onDeleteAccount={handleDeleteAccount}
              ownedClubs={(myClubs || []).filter(c => {
                if (!user) return false;
                if (c.ownerId && c.ownerId === user.uid) return true;
                const memberEntry = c.members?.[user.uid] || (c.members && Object.values(c.members).find(m => m.name === user.displayName));
                if (memberEntry && (memberEntry.role === 'owner' || memberEntry.role === 'admin')) return true;
                return false;
              })}
            />
          )}
        {showEventModal && (
          <EventModal
            key={eventToEdit?.id || 'new'}
            mode={eventModalMode}
            event={eventToEdit}
            allPlayers={allPlayers}
            onClose={handleCloseEventModal}
            onSave={handleSaveEvent}
            onEnd={(ev) => {
              handleCloseEventModal();
              setEventToEnd(ev);
            }}
            onDelete={(ev) => {
              handleCloseEventModal();
              handleRequestDeleteEvent(ev);
            }}
          />
        )}

        {eventToEnd && (
          <ConfirmationModal
            title="End event?"
            message={`End "${eventToEnd.name}" today? Its leaderboard is frozen and the leader is crowned champion.`}
            onConfirm={handleEndEvent}
            onCancel={() => setEventToEnd(null)}
          />
        )}
        {eventToDelete && (
          <ConfirmationModal
            title="Delete event?"
            message={`Delete "${eventToDelete.name}"? The event and its leaderboard are removed for everyone. Matches stay in history. This cannot be undone.`}
            onConfirm={handleDeleteEvent}
            onCancel={() => setEventToDelete(null)}
            />
          )}
          {!selectedClubId ? (
              <div className={`page-transition-container ${navigationDirection === 'backward' ? 'page-enter-left' : ''}`}>
              <section className="card mb-lg">
                {/* Tabs */}
                <div className="tabs-container mb-md" style={{ '--tab-count': '2' }}>
                  <div className="tabs-group">
                    <div 
                      className={`tab ${landingPageTab === 'my-clubs' ? 'tab-selected' : 'tab-unselected'}`}
                      onClick={() => setLandingPageTab('my-clubs')}
                    >
                      <div className="tab-contents">
                        <span className="tab-label">MY CLUBS</span>
                      </div>
                    </div>
                    <div 
                      className={`tab ${landingPageTab === 'create-join' ? 'tab-selected' : 'tab-unselected'}`}
                      onClick={() => setLandingPageTab('create-join')}
                    >
                      <div className="tab-contents">
                        <span className="tab-label">CREATE / JOIN</span>
                      </div>
                    </div>
                    {/* Single sliding indicator */}
                    <div 
                      className="tab-indicator"
                      style={{
                        left: landingPageTab === 'my-clubs' ? '0%' : '50%',
                        width: '50%'
                      }}
                    />
                  </div>
                </div>

                {/* Tab Content Container */}
                <div style={{ position: 'relative'}}>
                  {/* My Clubs Tab */}
                  <div 
                    key="my-clubs"
                    className={`tab-content ${landingPageTab === 'my-clubs' ? 'tab-content-active' : 'tab-content-inactive'}`}
                  >
                    {landingPageTab === 'my-clubs' && (
                      <div className="flex flex-col">
                        <ul className="list-container mt-md">
                          {myClubs.map(club => {
                            const clubOwnerId = club.ownerId;
                            const memberRole = club.members?.[user?.uid]?.role || (club.members && Object.values(club.members).find(m => m.name === user?.displayName)?.role) || 'member';
                            const isOwner = clubOwnerId === user?.uid || memberRole === 'owner';
                            const isAdmin = memberRole === 'admin';

                            return (
                              <li 
                                key={club.id} 
                                className="list-item relative"
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '12px'
                                }}
                              >
                                <div 
                                  onClick={() => {
                                    setNavigationDirection('forward');
                                    setSelectedClubId(club.id);
                                  }}
                                  className="cursor-pointer min-w-0"
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '16px',
                                    flex: 1
                                  }}
                                >
                                  <img
                                    src={club.photoUrl ? getResolvedAvatar(club.photoUrl) : getInitialsAvatar(club.name)}
                                    alt={`${club.name} logo`}
                                    className="avatar avatar-md avatar-border flex-shrink-0"
                                  />
                                  <div className="min-w-0 text-left" style={{ flex: 1 }}>
                                    <div className="list-item-title truncate">
                                      {club.name}
                                    </div>
                                    <div className="card-content caption">
                                      {club.playerCount} Players
                                    </div>
                                  </div>
                                </div>

                                {/* More Vert Icon Dropdown Menu */}
                                <div className="relative flex-shrink-0 ml-auto">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveClubMenuId(activeClubMenuId === club.id ? null : club.id);
                                    }}
                                    className="p-2 text-[var(--on-surface)] opacity-70 hover:opacity-100 transition-opacity rounded-full flex items-center justify-center"
                                    aria-label="Club options"
                                    title="Options"
                                  >
                                    <span className="material-symbols-outlined" style={{ fontSize: '24px', color: 'var(--on-surface)' }}>more_vert</span>
                                  </button>

                                  {activeClubMenuId === club.id && (
                                    <div className="absolute right-0 top-10 z-50 w-44 bg-[#0c152e] border border-white/10 rounded-2xl shadow-xl p-1.5 text-left">
                                      {(isOwner || isAdmin) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveClubMenuId(null);
                                            setClubToEdit(club);
                                            setShowEditClubModal(true);
                                          }}
                                          className="w-full text-left px-3 py-2 text-sm font-semibold text-white hover:bg-white/10 rounded-xl flex items-center gap-2"
                                        >
                                          <span className="material-symbols-outlined text-lg">edit</span>
                                          Edit club
                                        </button>
                                      )}

                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveClubMenuId(null);
                                          setClubToLeave(club);
                                          setShowLeaveClubModal(true);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm font-semibold text-red-400 hover:bg-red-500/10 rounded-xl flex items-center gap-2"
                                      >
                                        <span className="material-symbols-outlined text-lg">logout</span>
                                        Leave club
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Create / Join Tab */}
                  <div 
                    key="create-join"
                    className={`tab-content ${landingPageTab === 'create-join' ? 'tab-content-active' : 'tab-content-inactive'}`}
                  >
                    {landingPageTab === 'create-join' && (
                      <div className="flex flex-col items-center mt-lg w-full max-w-md mx-auto text-center">
                        <form
                          className="w-full flex flex-col items-center text-center"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            if (clubName.trim()) {
                              await handleCreateClub(e);
                            } else if (joinCode.trim()) {
                              await handleJoinClub(e);
                            }
                          }}
                        >
                          <div className="mb-6 w-full flex flex-col items-center">
                            <h3 className="headline-medium mb-md text-center">Create club</h3>
                            <input
                              type="text"
                              placeholder="New club name"
                              value={clubName}
                              onChange={(e) => setClubName(e.target.value)}
                              className="input text-center w-full mb-md"
                            />
                          </div>

                          <div className="mb-6 w-full flex flex-col items-center">
                            <h3 className="headline-medium mb-md text-center">Join a club</h3>
                            <input
                              type="text"
                              placeholder="Enter club join code"
                              value={joinCode}
                              onChange={(e) => setJoinCode(e.target.value)}
                              className="input text-center w-full mb-md"
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={!clubName.trim() && !joinCode.trim()}
                            className="btn btn-primary btn-full"
                          >
                            <span className="btn-text">LET'S GO</span>
                          </button>
                        </form>
                      </div>
                    )}
                    </div>
                  </div>
                </section>
              </div>
              ) : (
                <>
                {/* Player Selection if session is NOT active, otherwise Active Session View */}
                <div className={`page-transition-container ${navigationDirection === 'forward' ? 'page-enter-right' : 'page-enter-left'}`}>
                {!isSessionActive ? (
                  <>
                    {/* TAB 1: PLAY */}
                    {activeNavTab === 'play' && (
                      <section className={`club-card mb-[90px] ${navTabDirection === 'forward' ? 'tab-slide-enter-right' : 'tab-slide-enter-left'}`}>
                        {/* Club Badge and Header */}
                        <div className="club-badge-container">
                          {currentClubData?.photoUrl && (
                            <img
                              src={getResolvedAvatar(currentClubData.photoUrl)}
                              alt={`${currentClubData?.name} logo`}
                              className="club-badge play-build-badge"
                            />
                          )}
                        </div>
                        <h2 className="club-name play-build-name">{currentClubData?.name}</h2>
                        {(userRole === 'owner' || userRole === 'admin') && (
                           <button 
                            onClick={() => setShowEditClubModal(true)}
                            className="club-edit-button"
                            style={{ left: 'var(--spacing-md)', right: 'auto' }}
                            aria-label="Edit club"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '24px', color: 'var(--on-surface)' }}>edit</span>
                          </button>
                        )}
                          <button 
                          onClick={() => setShowInvitePlayersModal(true)}
                          className="club-edit-button"
                          style={{ right: 'var(--spacing-md)', left: 'auto' }}
                          aria-label="Invite players"
                          >
                          <span className="material-symbols-outlined" style={{ fontSize: '24px', color: 'var(--on-surface)' }}>person_add</span>
                          </button>
                          
                          {/* Unified Player List */}
                        <ul className="list-container">
                            {allPlayers.map((player, playerIndex) => {
                              // Find the corresponding club member to get their role and uid
                              const member = clubMembers.find(member => member.name === player.name);
                              
                              // Consolidate player data for the modal
                              const playerWithRole = { 
                                ...player, 
                                role: member?.role || 'guest', 
                                uid: member?.uid || null 
                              };
    
                              return (
                              <li key={player.name} className="list-item play-build-item" style={{ '--pi': Math.min(playerIndex, 10) }}>
                                  <div className="flex items-center justify-between">
                                    {/* Player Info (Tappable) */}
                                    <div
                                      onClick={() => setSelectedPlayerForCard(playerWithRole)}
                                      className="flex items-center space-x-4 cursor-pointer flex-grow"
                                    >
                                      <img
                                        src={getPlayerAvatar(allPlayers, player.name)}
                                        alt={`${player}'s avatar`}
                                      className="avatar avatar-md avatar-border"
                                      />
                                      <div>
                                      <div className="list-item-title flex items-center">
                                          {player.name}
                                        </div>
                                      <div className="card-content caption">
                                        {member 
                                          ? member.role === 'owner' 
                                            ? 'Club Owner' 
                                            : member.role === 'admin' 
                                              ? 'Admin' 
                                              : 'Club Member'
                                          : 'Guest Player'}
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Session Selection Checkbox */}
                                    <label className="inline-flex items-center space-x-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selectedPlayers.some(p => p.name === player.name)}
                                        onChange={() => {
                                          setSelectedPlayers(prevSelected => {
                                            if (prevSelected.some(p => p.name === player.name)) {
                                              return prevSelected.filter(p => p.name !== player.name);
                                            } else if (prevSelected.length < 6) {
                                              return [...prevSelected, player];
                                            }
                                            return prevSelected;
                                          });
                                        }}
                                      className="checkbox"
                                      />
                                    </label>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          
                        <div className="text-center mt-lg">
                            {selectedPlayers.length < 2 ? (
                              <button
                                disabled
                                className="btn btn-secondary btn-full cursor-not-allowed opacity-60"
                              >
                                <span className="btn-text">SELECT 2-6 PLAYERS</span>
                              </button>
                            ) : selectedPlayers.length === 2 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                <button
                                  onClick={() => setShowBestOfSetsModal(true)}
                                  className="btn btn-primary btn-full"
                                >
                                  <span className="btn-text">SINGLES</span>
                                </button>
                              </div>
                            ) : selectedPlayers.length === 3 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                <button
                                  onClick={() => handleStartSession('mvp')}
                                  className="btn btn-primary btn-full"
                                >
                                  <span className="btn-text">SINGLES MVP</span>
                                </button>
                              </div>
                            ) : selectedPlayers.length === 4 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                <button
                                  onClick={() => setShowBestOfSetsModal(true)}
                                  className="btn btn-primary btn-full"
                                >
                                  <span className="btn-text">DOUBLES</span>
                                </button>
                                <button
                                  onClick={() => handleStartSession('mvp')}
                                  className="btn btn-secondary btn-full"
                                >
                                  <span className="btn-text">DOUBLES MVP</span>
                                </button>
                              </div>
                            ) : selectedPlayers.length === 5 || selectedPlayers.length === 6 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                <button
                                  onClick={() => handleStartSession('mvp')}
                                  className="btn btn-primary btn-full"
                                >
                                  <span className="btn-text">DOUBLES MVP</span>
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </section>
                    )}

                    {/* TAB 2: STATS */}
                    {activeNavTab === 'stats' && (
                      <div className={`w-full max-w-[680px] mx-auto pb-[90px] ${navTabDirection === 'forward' ? 'tab-slide-enter-right' : 'tab-slide-enter-left'}`}>
                        <PlayerStatsTabs 
                          displayedSession={displayedSession}
                          overallStats={overallStats}
                          allPlayers={allPlayers}
                          currentArchivedSessionIndex={currentArchivedSessionIndex}
                          archivedSessions={archivedSessions}
                          handlePrevSession={handlePrevSession}
                          handleNextSession={handleNextSession}
                          handleRecalculateOverallStats={handleRecalculateOverallStats}
                          isRecalculating={isRecalculating}
                          onDeleteSession={confirmDeleteArchivedSession}
                          canEditArchivedSession={canDeleteArchivedSession}
                          isSessionActive={isSessionActive}
                          currentUser={user}
                        />
                      </div>
                    )}

                    {/* TAB 3: EVENTS (#50) */}
                    {activeNavTab === 'events' && (
                      <div className={`w-full max-w-[680px] mx-auto pb-[90px] ${navTabDirection === 'forward' ? 'tab-slide-enter-right' : 'tab-slide-enter-left'}`}>
                        <EventsView
                          events={events}
                          eventSummaries={eventSummaries}
                          allPlayers={allPlayers}
                          canManage={canManageEvents}
                          isSessionActive={isSessionActive}
                          isLoading={isEventsLoading}
                          focusSection={eventsFocusSection}
                          onFocusHandled={() => setEventsFocusSection(null)}
                          onCreate={openCreateEventModal}
                          onEdit={openEditEventModal}
                        />
                      </div>
                    )}

                    {/* TAB 4: INSIGHTS */}
                    {activeNavTab === 'insights' && (
                      <div className={`w-full max-w-[680px] mx-auto pb-[90px] ${navTabDirection === 'forward' ? 'tab-slide-enter-right' : 'tab-slide-enter-left'}`}>
                        <InsightsView 
                          overallInsights={overallInsights} 
                          allPlayers={allPlayers} 
                          overallStats={overallStats}
                          eventMvpCounts={eventMvpCounts}
                          onOpenPastEvents={() => {
                            setEventsFocusSection('past');
                            handleBottomTabChange('events');
                          }}
                          archivedSessions={archivedSessions}
                          clubName={currentClubData?.name}
                          clubPhotoUrl={currentClubData?.photoUrl}
                        />
                      </div>
                    )}

                    {/* PROFILE (opened from the header avatar) */}
                    {activeNavTab === 'profile' && (
                      <div className={`w-full max-w-[680px] mx-auto pb-[90px] ${navTabDirection === 'forward' ? 'tab-slide-enter-right' : 'tab-slide-enter-left'}`}>
                        <ProfileView
                          user={user}
                          userProfile={{ name: resolveSelfName(currentClubData, user?.uid, user?.displayName) }}
                          allPlayers={allPlayers}
                          overallStats={overallStats}
                          playerStreaks={playerStreaks}
                          eventMvpCounts={eventMvpCounts}
                          badges={playerBadges}
                          onOpenAchievements={() => setShowAchievements(true)}
                        />
                        <ProfileMoreModal
                          open={showProfileMenu}
                          onClose={() => setShowProfileMenu(false)}
                          onOpenClubs={() => setSelectedClubId(null)}
                          onSignOut={handleSignOut}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="fixed inset-0 z-50 w-screen h-screen bg-[#080e21] overflow-hidden flex flex-col font-sans select-none animate-fade-in">
                    {sessionMode === 'classic' ? (
                      <LiveSessionClassic
                        classicMatch={gameRotation[0]}
                        classicScore={classicScore}
                        sessionVersion={sessionVersion}
                        classicPointHistory={classicPointHistory}
                        classicGameHistory={classicGameHistory}
                        bestOfSets={bestOfSets}
                        goldenPoint={goldenPoint}
                        onToggleGoldenPoint={handleToggleGoldenPoint}
                        clubName={currentClubData?.name}
                        db={db}
                        selectedClubId={selectedClubId}
                        appId={appId}
                        allPlayers={allPlayers}
                        onMatchWin={handleClassicMatchWin}
                        onFinishSession={confirmEndSession}
                      />
                    ) : (
                      <LiveSession
                        currentGame={currentGame}
                        nextGame={nextGame}
                        currentServingPlayer={currentServingPlayer}
                        nextServingPlayer={nextUpServingPlayer}
                        allPlayers={allPlayers}
                        onTeamWin={recordGameResult}
                        sessionPlayers={sessionPlayers}
                        currentSessionStats={currentSessionStats}
                        sessionHistory={sessionHistory}
                        gameRotation={gameRotation}
                        goldenPoint={goldenPoint}
                        onToggleGoldenPoint={handleToggleGoldenPoint}
                        onPointScored={handleMvpPointScored}
                        mvpLiveScore={mvpLiveScore}
                        onFinishSession={confirmEndSession}
                        db={db}
                        selectedClubId={selectedClubId}
                        appId={appId}
                        clubName={currentClubData?.name}
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        {!isSessionActive && selectedClubId && user && (
          <BottomNavBar
            activeTab={activeNavTab}
            onTabChange={handleBottomTabChange}
          />
        )}
      </div>
    </div>
  );
}

export default App;